import { coverSvgSized, type CoverId } from './covers';
import {
  ART_PAL,
  ball,
  body,
  edge,
  fade,
  gearPath,
  gloss,
  mix,
  n,
  rng,
  shade,
  shadow,
  shadowInk,
  star,
  tint,
  type ArtPal,
  type ArtTheme,
} from './covers/kit';
import { currentScale, rasterArt, rasterSymbolByName, type RasterScale } from './raster';
import { NEON_DRAW } from './symbols-neon';

/*
 * Slot symbols — production vector art in the style of the covers (./covers/kit): soft volume (3–4 stop
 * gradients), one light source top-left, a gloss, a soft ground shadow and a neat outline in the object's own
 * darker tone. Special symbols (wilds, scatters, bonus) sit on a gold-rimmed plate with a glow: wilds on a
 * rounded tile, scatters / bonus on a round medal. viewBox 0 0 100 100, readable at 48 px, no text.
 * Raster art from `pnpm art:process` (raster.gen.ts) wins over these when present (symbolUri).
 * Every <defs> id is prefixed by symbol + theme (`s-<name>-<t>-`), so the markup can be inlined next to other
 * symbols / covers in one SVG document.
 */
export type { ArtTheme, CoverId };

type Draw = (P: ArtPal, p: string) => string;

const INK = '#141A26';
/** sunny gold: the light-theme gold reads brown on symbols, so light uses a clear honey tone */
const warm = (P: ArtPal) => (P.dark ? P.gold : mix(tint(P.gold, 0.3), '#FFD166', 0.55));
const pale = (P: ArtPal) => (P.dark ? '#E4EAF4' : '#F4F6FA');
const steel = (P: ArtPal) => (P.dark ? mix(P.blue, '#E4EAF4', 0.55) : tint(P.blue, 0.55));
const wood = (P: ArtPal) => shade(mix(P.orange, P.gold, 0.3), P.dark ? 0.42 : 0.35);

/** outline in the object's own darker tone */
const ol = (c: string, P: ArtPal, w: number | string = 2) =>
  `stroke="${edge(c, P)}" stroke-width="${w}" stroke-linejoin="round" stroke-linecap="round"`;
const fl = (p: string, id: string) => `fill="url(#${p}${id})"`;
const D = (...g: string[]) => `<defs>${g.join('')}</defs>`;
const drop = (P: ArtPal, p: string) => fade(`${p}s`, shadowInk(P), P.dark ? 0.6 : 0.24);
const ground = (p: string, cx = 50, cy = 91, rx = 28, ry = 5) => shadow(`${p}s`, cx, cy, rx, ry);
const eye = (x: number, y: number, r: number) =>
  `<circle cx="${n(x)}" cy="${n(y)}" r="${n(r)}" fill="#FFFFFF" stroke="${INK}" stroke-opacity=".35" stroke-width=".8"/>` +
  `<circle cx="${n(x - r * 0.2)}" cy="${n(y + r * 0.05)}" r="${n(r * 0.58)}" fill="${INK}"/>` +
  `<circle cx="${n(x - r * 0.42)}" cy="${n(y - r * 0.28)}" r="${n(r * 0.22)}" fill="#FFFFFF"/>`;
/** cover leaf (kit style), `l` gradient */
const leaf = (p: string, P: ArtPal, x: number, y: number, rot: number, s: number) =>
  `<path d="M0 0C-6 -18 -2 -36 10 -46C16 -30 14 -12 0 0Z" transform="translate(${x} ${y}) rotate(${rot}) scale(${s})" ${fl(p, 'l')} ${ol(P.green, P, n(1.8 / s))}/>`;
const leafDef = (P: ArtPal, p: string) => body(`${p}l`, P.green, 0.35, 0.3, 0.6, 1);
const ring = (id: string, c: string) =>
  `<linearGradient id="${id}" x1="0" y1="0" x2=".7" y2="1"><stop offset="0" stop-color="${tint(c, 0.6)}"/>` +
  `<stop offset=".45" stop-color="${c}"/><stop offset="1" stop-color="${shade(c, 0.45)}"/></linearGradient>`;
const scale = (k: number, inner: string, cx = 50, cy = 50) =>
  `<g transform="translate(${cx} ${cy}) scale(${k}) translate(${-cx} ${-cy})">${inner}</g>`;

/**
 * Plate of a special symbol: glow of `c`, a tinted disc (medal) or rounded tile (wild) with a gold rim,
 * an inner bevel and a gloss; `inner` (the object) is drawn scaled to `k` on top.
 */
function plate(P: ArtPal, p: string, c: string, shape: 'tile' | 'medal', inner: string, k = 0.78): string {
  const disc = P.dark ? mix(c, P.bg, 0.7) : tint(c, 0.8);
  const bevel = `fill="none" stroke="#FFFFFF" stroke-opacity="${P.dark ? 0.14 : 0.7}" stroke-width="1.3"`;
  const rim = `fill="url(#${p}m)" stroke="url(#${p}r)" stroke-width="4"`;
  return (
    D(
      fade(`${p}g`, c, P.dark ? 0.7 : 0.55),
      ball(`${p}m`, disc, P.dark ? 0.2 : 0.55, P.dark ? 0.45 : 0.18),
      ring(`${p}r`, warm(P)),
    ) +
    `<circle cx="50" cy="50" r="50" fill="url(#${p}g)"/>` +
    (shape === 'medal'
      ? `<circle cx="50" cy="50" r="43" ${rim}/><circle cx="50" cy="50" r="38.5" ${bevel}/>`
      : `<rect x="7" y="7" width="86" height="86" rx="20" ${rim}/><rect x="11.5" y="11.5" width="77" height="77" rx="16" ${bevel}/>`) +
    gloss(30, 22, 14, 5, -28, P.dark ? 0.16 : 0.55) +
    scale(k, inner)
  );
}

/* ---------------- Garden ---------------- */

const carrot: Draw = (P, p) =>
  D(drop(P, p), body(`${p}b`, P.orange, 0.38, 0.32, 1, 0.4), leafDef(P, p)) +
  ground(p, 57, 91, 18, 4) +
  `<g transform="rotate(-20 50 55)">` +
  leaf(p, P, 45, 35, -38, 0.5) +
  leaf(p, P, 51, 33, 4, 0.6) +
  leaf(p, P, 55, 35, 42, 0.48) +
  `<path d="M30 36Q50 28 70 36L53 88Q50 93 47 88Z" ${fl(p, 'b')} ${ol(P.orange, P)}/>` +
  `<path d="M36 48H46M54 58H62M40 68H48M49 79H54" fill="none" stroke="${edge(P.orange, P)}" stroke-width="2.2" stroke-linecap="round" stroke-opacity=".55"/>` +
  gloss(40, 40, 8, 3, -6, 0.55) +
  `</g>`;

const tomato: Draw = (P, p) =>
  D(drop(P, p), ball(`${p}b`, P.red, 0.45, 0.42), leafDef(P, p)) +
  ground(p, 52, 91, 28, 5) +
  `<circle cx="50" cy="57" r="33" ${fl(p, 'b')} ${ol(P.red, P)}/>` +
  `<path d="M36 30Q30 46 34 60" fill="none" stroke="${edge(P.red, P)}" stroke-width="1.6" stroke-opacity=".3" stroke-linecap="round"/>` +
  `<path d="M50 27Q49 18 55 12" fill="none" stroke="${shade(P.green, 0.25)}" stroke-width="4" stroke-linecap="round"/>` +
  `<path d="M0 -40L7 -52L10 -38L22 -44L14 -32L-14 -32L-22 -44L-10 -38L-7 -52Z" transform="translate(50 64) scale(.8)" ${fl(p, 'l')} ${ol(P.green, P, 2.2)}/>` +
  gloss(37, 45, 10, 5.5, -40, 0.6) +
  gloss(64, 72, 3, 1.6, -40, 0.25);

const pumpkin: Draw = (P, p) => {
  const lobe = (cx: number, rx: number, ry: number) =>
    `<ellipse cx="${cx}" cy="0" rx="${rx}" ry="${ry}" ${fl(p, 'b')} ${ol(P.orange, P, 3.6)}/>`;
  return (
    D(drop(P, p), ball(`${p}b`, P.orange, 0.42, 0.42), leafDef(P, p)) +
    ground(p, 52, 88, 34, 5) +
    `<g transform="translate(50 60) scale(.52)">` +
    lobe(-34, 34, 46) +
    lobe(34, 34, 46) +
    lobe(0, 36, 50) +
    `<path d="M-14 -46Q-22 0 -14 46M14 -46Q22 0 14 46" fill="none" stroke="${edge(P.orange, P)}" stroke-width="3" stroke-opacity=".4" stroke-linecap="round"/>` +
    `<path d="M-4 -46C-6 -60 -2 -70 10 -76" fill="none" stroke="${edge(P.green, P)}" stroke-width="14" stroke-linecap="round"/>` +
    `<path d="M-4 -46C-6 -60 -2 -70 10 -76" fill="none" stroke="${shade(P.green, 0.1)}" stroke-width="9" stroke-linecap="round"/>` +
    leaf(p, P, 8, -52, 62, 1) +
    gloss(-14, -26, 13, 6, -40, 0.55) +
    gloss(-50, -18, 6, 10, -20, 0.3) +
    `</g>`
  );
};

const radish: Draw = (P, p) => {
  const c = mix(P.pink, P.red, 0.3);
  return (
    D(drop(P, p), ball(`${p}b`, c, 0.45, 0.4), leafDef(P, p)) +
    ground(p, 52, 92, 20, 4) +
    leaf(p, P, 46, 38, -42, 0.56) +
    leaf(p, P, 50, 37, 2, 0.62) +
    leaf(p, P, 54, 38, 44, 0.54) +
    `<path d="M50 84Q51 90 48 96" fill="none" stroke="${edge(c, P)}" stroke-width="2.2" stroke-linecap="round"/>` +
    `<path d="M50 36C76 36 82 62 50 86C18 62 24 36 50 36Z" ${fl(p, 'b')} ${ol(c, P)}/>` +
    `<path d="M39 73Q50 79 61 73Q56 81 50 86Q44 81 39 73Z" fill="${pale(P)}" fill-opacity=".75"/>` +
    gloss(39, 49, 9, 5, -40, 0.6)
  );
};

const corn: Draw = (P, p) => {
  const g = warm(P);
  return (
    D(
      drop(P, p),
      ball(`${p}b`, g, 0.45, 0.35),
      leafDef(P, p),
      `<clipPath id="${p}c"><ellipse cx="50" cy="44" rx="15" ry="31"/></clipPath>`,
    ) +
    ground(p, 50, 93, 20, 4) +
    `<ellipse cx="50" cy="44" rx="15" ry="31" ${fl(p, 'b')} ${ol(g, P)}/>` +
    `<g clip-path="url(#${p}c)" fill="none" stroke="${edge(g, P)}" stroke-opacity=".45" stroke-width="1.4">` +
    `<path d="M34 22H66M34 30H66M34 38H66M34 46H66M34 54H66M34 62H66"/>` +
    `<path d="M43 12Q40 44 43 76M50 12V76M57 12Q60 44 57 76"/></g>` +
    gloss(44, 28, 4, 10, -12, 0.6) +
    `<path d="M50 92C30 86 22 66 30 38C38 58 44 68 50 72Z" ${fl(p, 'l')} ${ol(P.green, P)}/>` +
    `<path d="M50 92C70 86 78 66 70 38C62 58 56 68 50 72Z" ${fl(p, 'l')} ${ol(P.green, P)}/>` +
    `<path d="M34 50Q38 68 48 80" fill="none" stroke="#FFFFFF" stroke-opacity=".35" stroke-width="1.6" stroke-linecap="round"/>`
  );
};

const can: Draw = (P, p) => {
  const c = P.sky;
  return (
    D(drop(P, p), body(`${p}b`, c, 0.4, 0.35, 0.4, 1), ball(`${p}h`, tint(c, 0.2), 0.5, 0.3)) +
    ground(p, 50, 91, 32, 5) +
    `<path d="M32 46Q32 22 50 22Q68 22 67 44" fill="none" stroke="${edge(c, P)}" stroke-width="8" stroke-linecap="round"/>` +
    `<path d="M32 46Q32 22 50 22Q68 22 67 44" fill="none" stroke="${tint(c, 0.15)}" stroke-width="4.5" stroke-linecap="round"/>` +
    `<path d="M68 58L86 34L91 38L74 68Z" ${fl(p, 'b')} ${ol(c, P)}/>` +
    `<ellipse cx="89" cy="35" rx="5" ry="7.5" transform="rotate(38 89 35)" fill="url(#${p}h)" ${ol(c, P)}/>` +
    `<path d="M28 44H68Q72 44 72 48V83Q72 89 66 89H30Q24 89 24 83V48Q24 44 28 44Z" ${fl(p, 'b')} ${ol(c, P)}/>` +
    `<path d="M24 54H72M24 58H72" stroke="${edge(c, P)}" stroke-opacity=".35" stroke-width="2"/>` +
    gloss(33, 68, 3.5, 11, 0, 0.5) +
    `<circle cx="93" cy="48" r="2.2" fill="${tint(c, 0.3)}"/><circle cx="88" cy="53" r="1.8" fill="${tint(c, 0.3)}"/>`
  );
};

const scarecrow: Draw = (P, p) => {
  const hat = mix(P.gold, P.orange, 0.35);
  const face = P.dark ? mix(P.gold, '#E4EAF4', 0.45) : tint(mix(P.gold, P.orange, 0.2), 0.45);
  const straw = `fill="none" stroke="${warm(P)}" stroke-width="2.2" stroke-linecap="round"`;
  return plate(
    P,
    p,
    P.orange,
    'tile',
    D(
      drop(P, p),
      body(`${p}b`, P.red, 0.38, 0.35, 0.4, 1),
      ball(`${p}f`, face, 0.4, 0.3),
      body(`${p}h`, hat, 0.4, 0.4, 0.4, 1),
      body(`${p}w`, wood(P), 0.3, 0.3, 1, 0.2),
    ) +
      ground(p, 50, 94, 20, 3.5) +
      `<rect x="47" y="46" width="6" height="48" rx="2" fill="url(#${p}w)" ${ol(wood(P), P, 1.5)}/>` +
      `<rect x="12" y="48" width="76" height="6" rx="2" fill="url(#${p}w)" ${ol(wood(P), P, 1.5)}/>` +
      `<path d="M14 48l-4 -4M14 54l-4 4M86 48l4 -4M86 54l4 4" ${straw}/>` +
      `<path d="M33 47H67L64 58L71 80H29L36 58Z" ${fl(p, 'b')} ${ol(P.red, P)}/>` +
      `<path d="M33 47L20 50V60L36 57Z M67 47L80 50V60L64 57Z" ${fl(p, 'b')} ${ol(P.red, P)}/>` +
      `<rect x="54" y="64" width="9" height="9" rx="1.5" transform="rotate(8 58 68)" fill="${P.blue}" ${ol(P.blue, P, 1.2)}/>` +
      `<path d="M50 50V78" stroke="${edge(P.red, P)}" stroke-opacity=".45" stroke-width="1.6"/>` +
      `<path d="M34 80l-2 6M42 80v6M58 80v6M66 80l2 6" ${straw}/>` +
      `<circle cx="50" cy="35" r="12" fill="url(#${p}f)" ${ol(face, P)}/>` +
      `<circle cx="45.5" cy="34" r="1.9" fill="${INK}"/><circle cx="54.5" cy="34" r="1.9" fill="${INK}"/>` +
      `<path d="M45 40Q50 43.5 55 40" fill="none" stroke="${INK}" stroke-width="1.6" stroke-linecap="round"/>` +
      `<ellipse cx="50" cy="25" rx="21" ry="4.5" fill="url(#${p}h)" ${ol(hat, P)}/>` +
      `<path d="M39 25Q39 9 50 9Q61 9 61 25Z" fill="url(#${p}h)" ${ol(hat, P)}/>` +
      `<path d="M39.5 21H60.5" stroke="${P.red}" stroke-width="3"/>` +
      gloss(44, 15, 4, 2.2, -30, 0.55) +
      gloss(40, 39, 25, 3, -12, 0.3),
    0.82,
  );
};

const sun: Draw = (P, p) => {
  const g = warm(P);
  let rays = '';
  for (let i = 0; i < 12; i++)
    rays += `<rect x="46" y="11" width="8" height="17" rx="4" transform="rotate(${i * 30} 50 50)" fill="url(#${p}y)" ${ol(g, P, 1.4)}/>`;
  return plate(
    P,
    p,
    P.gold,
    'medal',
    D(ball(`${p}b`, g, 0.55, 0.3), body(`${p}y`, g, 0.45, 0.25, 0.3, 1)) +
      rays +
      `<circle cx="50" cy="50" r="22" fill="url(#${p}b)" ${ol(g, P)}/>` +
      `<circle cx="50" cy="50" r="16" fill="none" stroke="#FFFFFF" stroke-opacity=".3" stroke-width="1.5"/>` +
      gloss(42, 41, 8, 4.5, -40, 0.65),
    0.86,
  );
};

/* ---------------- Deep ---------------- */

const shell: Draw = (P, p) =>
  D(drop(P, p), body(`${p}b`, P.pink, 0.45, 0.32, 0.4, 1)) +
  ground(p, 50, 92, 30, 4.5) +
  `<path d="M41 82H59L57 91H43Z" ${fl(p, 'b')} ${ol(P.pink, P)}/>` +
  `<path d="M50 84L13 48Q11 40 19 36Q19 26 29 24Q33 15 42 17Q50 11 58 17Q67 15 71 24Q81 26 81 36Q89 40 87 48Z" ${fl(p, 'b')} ${ol(P.pink, P)}/>` +
  `<path d="M50 84L20 38M50 84L32 25M50 84L50 16M50 84L68 25M50 84L80 38" fill="none" stroke="${edge(P.pink, P)}" stroke-width="1.8" stroke-opacity=".5" stroke-linecap="round"/>` +
  gloss(35, 33, 9, 4, -35, 0.55) +
  gloss(58, 26, 5, 2.4, -20, 0.35);

const starfish: Draw = (P, p) =>
  D(drop(P, p), body(`${p}b`, P.orange, 0.4, 0.35, 0.6, 1)) +
  ground(p, 52, 90, 30, 5) +
  `<polygon points="${star(50, 53, 41, 18, 5, -Math.PI / 2 + 0.12)}" ${fl(p, 'b')} ${ol(P.orange, P, 2.6)}/>` +
  [
    [50, 53],
    [52, 33],
    [68, 47],
    [33, 45],
    [40, 66],
    [61, 66],
  ]
    .map(([x, y]) => `<circle cx="${x}" cy="${y}" r="2.3" fill="${tint(P.orange, 0.55)}"/>`)
    .join('') +
  gloss(43, 40, 7, 3.5, -40, 0.55);

const jelly: Draw = (P, p) => {
  const t = (x: number, d: number) =>
    `<path d="M${x} 52q${d} 10 0 20t0 18" fill="none" stroke="${tint(P.purple, 0.25)}" stroke-width="4" stroke-linecap="round" stroke-opacity=".85"/>`;
  return (
    D(drop(P, p), ball(`${p}b`, P.purple, 0.48, 0.35)) +
    ground(p, 50, 94, 22, 3.5) +
    t(29, -5) +
    t(42, 5) +
    t(58, -5) +
    t(71, 5) +
    `<path d="M15 52C15 8 85 8 85 52Q76 45 67.5 52Q59 45 50 52Q41 45 32.5 52Q24 45 15 52Z" ${fl(p, 'b')} ${ol(P.purple, P)}/>` +
    `<path d="M22 44Q50 34 78 44" fill="none" stroke="#FFFFFF" stroke-opacity=".3" stroke-width="2" stroke-linecap="round"/>` +
    `<circle cx="62" cy="30" r="3" fill="#FFFFFF" fill-opacity=".35"/><circle cx="70" cy="37" r="2" fill="#FFFFFF" fill-opacity=".3"/>` +
    gloss(34, 27, 11, 5, -35, 0.6)
  );
};

/** fishA: classic oval fish with a forked tail (cover fish) */
const fishA: Draw = (P, p) => {
  const c = P.teal;
  const o = ol(c, P, 2.8);
  return (
    D(drop(P, p), body(`${p}b`, c, 0.42, 0.38, 0.4, 1)) +
    ground(p, 52, 90, 32, 4) +
    `<g transform="translate(47 52) scale(.72)">` +
    `<path d="M26 0L50 -18Q54 -20 52 -14L44 0L52 14Q54 20 50 18Z" ${fl(p, 'b')} ${o}/>` +
    `<path d="M-2 -28Q10 -44 22 -26" ${fl(p, 'b')} ${o}/>` +
    `<path d="M-40 0C-40 -22 -12 -32 12 -26C24 -22 32 -10 32 0C32 10 24 22 12 26C-12 32 -40 22 -40 0Z" ${fl(p, 'b')} ${o}/>` +
    `<path d="M4 -18Q12 0 4 18" fill="none" stroke="${edge(c, P)}" stroke-width="2.6" stroke-opacity=".5" stroke-linecap="round"/>` +
    `<path d="M-6 8Q2 14 -2 20" fill="none" stroke="${edge(c, P)}" stroke-width="2.4" stroke-opacity=".5" stroke-linecap="round"/>` +
    eye(-22, -5, 7) +
    gloss(-12, -17, 15, 4.5, -12, 0.5) +
    `</g>`
  );
};

/** fishB: tall angelfish — disc body, long dorsal/ventral fins, stripes */
const fishB: Draw = (P, p) => {
  const c = P.blue;
  return (
    D(
      drop(P, p),
      body(`${p}b`, c, 0.45, 0.38, 0.4, 1),
      `<clipPath id="${p}c"><ellipse cx="45" cy="52" rx="26" ry="23"/></clipPath>`,
    ) +
    ground(p, 50, 93, 26, 3.5) +
    `<path d="M34 34Q42 4 62 34Z" ${fl(p, 'b')} ${ol(c, P)}/>` +
    `<path d="M34 70Q42 100 62 70Z" ${fl(p, 'b')} ${ol(c, P)}/>` +
    `<path d="M66 52L86 36Q91 52 86 68Z" ${fl(p, 'b')} ${ol(c, P)}/>` +
    `<ellipse cx="45" cy="52" rx="26" ry="23" ${fl(p, 'b')} ${ol(c, P)}/>` +
    `<g clip-path="url(#${p}c)" fill="none" stroke="${P.dark ? pale(P) : tint(c, 0.75)}" stroke-width="5" stroke-opacity=".55"><path d="M42 26Q48 52 42 78M56 26Q62 52 56 78"/></g>` +
    eye(30, 48, 5.2) +
    gloss(34, 38, 9, 3.5, -25, 0.5)
  );
};

/** fishC: round pufferfish with spikes */
const fishC: Draw = (P, p) => {
  const c = warm(P);
  return (
    D(drop(P, p), ball(`${p}b`, c, 0.5, 0.38)) +
    ground(p, 48, 90, 28, 4) +
    `<polygon points="${star(46, 52, 36, 27, 14)}" fill="${shade(c, 0.12)}" ${ol(c, P, 1.8)}/>` +
    `<path d="M72 52L88 41Q92 52 88 63Z" fill="url(#${p}b)" ${ol(c, P)}/>` +
    `<circle cx="46" cy="52" r="28" fill="url(#${p}b)" ${ol(c, P)}/>` +
    `<path d="M26 64Q46 80 66 64" fill="none" stroke="${pale(P)}" stroke-width="5" stroke-opacity=".45" stroke-linecap="round"/>` +
    eye(33, 46, 6.5) +
    `<ellipse cx="22" cy="58" rx="2.6" ry="2" fill="${edge(c, P)}"/>` +
    `<path d="M52 56Q58 60 60 54" fill="none" stroke="${edge(c, P)}" stroke-width="2" stroke-opacity=".6" stroke-linecap="round"/>` +
    gloss(40, 35, 9, 4, -35, 0.6)
  );
};

const pearl: Draw = (P, p) => {
  const c = P.pink;
  const pr = P.dark ? '#EEF1F8' : '#FFFFFF';
  return (
    D(
      drop(P, p),
      body(`${p}b`, c, 0.45, 0.35, 0.4, 1),
      body(`${p}t`, shade(c, 0.15), 0.3, 0.35, 0.4, 1),
      ball(`${p}q`, mix(pr, P.sky, 0.12), 0.6, 0.28),
    ) +
    ground(p, 50, 92, 36, 4.5) +
    `<path d="M14 56Q14 16 50 14Q86 16 86 56Z" fill="url(#${p}t)" ${ol(c, P)}/>` +
    `<path d="M50 54L30 22M50 54L50 16M50 54L70 22" fill="none" stroke="${edge(c, P)}" stroke-width="1.6" stroke-opacity=".45" stroke-linecap="round"/>` +
    `<circle cx="50" cy="56" r="16" fill="url(#${p}q)" stroke="${edge(pr, P)}" stroke-width="1.6"/>` +
    gloss(44, 50, 5, 3, -40, 0.9) +
    `<path d="M8 60Q12 56 20 60Q35 64 50 64Q65 64 80 60Q88 56 92 60Q84 90 50 90Q16 90 8 60Z" ${fl(p, 'b')} ${ol(c, P)}/>` +
    `<path d="M24 70Q50 80 76 70" fill="none" stroke="${edge(c, P)}" stroke-width="1.6" stroke-opacity=".4" stroke-linecap="round"/>` +
    gloss(26, 68, 8, 2.5, -8, 0.5)
  );
};

const bubble: Draw = (P, p) =>
  plate(
    P,
    p,
    P.sky,
    'medal',
    D(
      `<radialGradient id="${p}b" cx=".38" cy=".32" r=".72"><stop offset="0" stop-color="#FFFFFF" stop-opacity=".3"/>` +
        `<stop offset=".6" stop-color="${P.sky}" stop-opacity=".12"/><stop offset=".88" stop-color="${P.sky}" stop-opacity=".35"/>` +
        `<stop offset="1" stop-color="${tint(P.sky, 0.5)}" stop-opacity=".7"/></radialGradient>`,
    ) +
      `<circle cx="50" cy="50" r="34" fill="url(#${p}b)" stroke="${tint(P.sky, 0.35)}" stroke-width="2.4"/>` +
      `<path d="M26 40Q30 24 46 19" fill="none" stroke="#FFFFFF" stroke-opacity=".85" stroke-width="4.5" stroke-linecap="round"/>` +
      `<circle cx="27" cy="50" r="2.4" fill="#FFFFFF" fill-opacity=".8"/>` +
      `<path d="M70 72Q76 66 78 58" fill="none" stroke="#FFFFFF" stroke-opacity=".35" stroke-width="2.4" stroke-linecap="round"/>` +
      `<circle cx="83" cy="22" r="6" fill="url(#${p}b)" stroke="${tint(P.sky, 0.35)}" stroke-width="1.6"/>` +
      `<circle cx="81" cy="20" r="1.6" fill="#FFFFFF" fill-opacity=".85"/>`,
    0.9,
  );

/* ---------------- Spaceport ---------------- */

/** planet body (r, colour) with bands clipped inside and a gloss */
const orb = (P: ArtPal, p: string, c: string, cx: number, cy: number, r: number, bands: string) =>
  `<clipPath id="${p}c"><circle cx="${cx}" cy="${cy}" r="${r}"/></clipPath>` +
  `<circle cx="${cx}" cy="${cy}" r="${r}" ${fl(p, 'b')} ${ol(c, P)}/>` +
  `<g clip-path="url(#${p}c)" fill="none" stroke-linecap="round">${bands}</g>` +
  gloss(cx - r * 0.38, cy - r * 0.45, r * 0.34, r * 0.16, -38, 0.5);

/** planetR: rocky, craters, no ring */
const planetR: Draw = (P, p) => {
  const c = P.red;
  const cr = (x: number, y: number, r: number) =>
    `<circle cx="${x}" cy="${y}" r="${r}" fill="${shade(c, 0.28)}" fill-opacity=".55"/><path d="M${n(x - r * 0.7)} ${n(y + r * 0.5)}A${r} ${r} 0 0 0 ${n(x + r * 0.7)} ${n(y + r * 0.5)}" fill="none" stroke="${tint(c, 0.4)}" stroke-opacity=".6" stroke-width="1.4"/>`;
  return (
    D(drop(P, p), ball(`${p}b`, c, 0.42, 0.48)) +
    ground(p, 52, 91, 26, 4) +
    orb(
      P,
      p,
      c,
      50,
      52,
      32,
      `<path d="M16 40Q50 30 84 44" stroke="${tint(c, 0.25)}" stroke-width="5" stroke-opacity=".4"/>`,
    ) +
    cr(62, 42, 6) +
    cr(40, 64, 7.5) +
    cr(64, 68, 4) +
    cr(34, 44, 3)
  );
};

/** ringed planet; `tilt` sets the ring angle so the two ringed planets read differently */
const ringed =
  (c: string, rc: (P: ArtPal) => string, tilt: number, rx: number, ry: number, r: number): Draw =>
  (P, p) => {
    const arc = (front: boolean) =>
      `<path d="M${front ? 50 + rx : 50 - rx} 52A${rx} ${ry} 0 0 1 ${front ? 50 - rx : 50 + rx} 52" transform="rotate(${tilt} 50 52)" fill="none" stroke="${edge(rc(P), P)}" stroke-width="8.5" stroke-linecap="round"/>` +
      `<path d="M${front ? 50 + rx : 50 - rx} 52A${rx} ${ry} 0 0 1 ${front ? 50 - rx : 50 + rx} 52" transform="rotate(${tilt} 50 52)" fill="none" stroke="url(#${p}k)" stroke-width="5.5" stroke-linecap="round"/>`;
    return (
      D(
        drop(P, p),
        ball(`${p}b`, c === 'blue' ? P.blue : P.orange, 0.45, 0.45),
        `<linearGradient id="${p}k" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${tint(rc(P), 0.45)}"/><stop offset=".5" stop-color="${rc(P)}"/><stop offset="1" stop-color="${shade(rc(P), 0.3)}"/></linearGradient>`,
      ) +
      ground(p, 52, 92, 28, 4) +
      arc(false) +
      orb(
        P,
        p,
        c === 'blue' ? P.blue : P.orange,
        50,
        52,
        r,
        c === 'blue'
          ? `<path d="M18 44Q50 36 82 46" stroke="${tint(P.blue, 0.35)}" stroke-width="5" stroke-opacity=".5"/><path d="M18 62Q50 54 82 64" stroke="${shade(P.blue, 0.2)}" stroke-width="6" stroke-opacity=".4"/>`
          : `<path d="M14 38Q50 30 86 40" stroke="${tint(P.orange, 0.35)}" stroke-width="6" stroke-opacity=".55"/><path d="M14 54Q50 46 86 56" stroke="${mix(P.orange, P.red, 0.45)}" stroke-width="8" stroke-opacity=".45"/><path d="M14 68Q50 62 86 70" stroke="${tint(P.orange, 0.2)}" stroke-width="5" stroke-opacity=".4"/>`,
      ) +
      arc(true)
    );
  };

/** planetB: blue, flat wide ring */
const planetB = ringed('blue', (P) => (P.dark ? pale(P) : tint(P.sky, 0.3)), -16, 44, 11, 25);
/** planetO: orange gas giant, steep ring */
const planetO = ringed('orange', (P) => warm(P), -62, 42, 10, 26);

/** planetG: green planet with a small moon on an orbit */
const planetG: Draw = (P, p) => {
  const c = P.green;
  return (
    D(drop(P, p), ball(`${p}b`, c, 0.45, 0.48), ball(`${p}o`, P.dark ? '#C9D2E3' : '#B8C2D4', 0.5, 0.4)) +
    ground(p, 46, 92, 26, 4) +
    `<ellipse cx="50" cy="52" rx="44" ry="16" transform="rotate(-24 50 52)" fill="none" stroke="${P.dark ? pale(P) : edge(c, P)}" stroke-opacity=".35" stroke-width="1.4" stroke-dasharray="3 4"/>` +
    orb(
      P,
      p,
      c,
      45,
      56,
      29,
      `<path d="M20 44Q34 40 40 48Q46 56 60 52" stroke="${shade(c, 0.22)}" stroke-width="7" stroke-opacity=".4"/><path d="M26 70Q40 64 52 72" stroke="${shade(c, 0.22)}" stroke-width="6" stroke-opacity=".35"/>`,
    ) +
    `<circle cx="83" cy="26" r="9" fill="url(#${p}o)" ${ol('#B8C2D4', P, 1.6)}/>` +
    `<circle cx="86" cy="29" r="2" fill="#000000" fill-opacity=".15"/>` +
    gloss(80, 22, 3, 1.8, -38, 0.7)
  );
};

const satellite: Draw = (P, p) => {
  const g = warm(P);
  const panel = (x: number) =>
    `<rect x="${x}" y="40" width="26" height="20" rx="2" fill="url(#${p}p)" ${ol(P.blue, P)}/>` +
    `<path d="M${x + 9} 40V60M${x + 17.5} 40V60M${x} 50H${x + 26}" stroke="${tint(P.blue, 0.5)}" stroke-opacity=".6" stroke-width="1.2"/>`;
  return (
    D(
      drop(P, p),
      body(`${p}b`, g, 0.45, 0.4, 0.5, 1),
      body(`${p}p`, P.blue, 0.35, 0.4, 0.5, 1),
      ball(`${p}d`, steel(P), 0.5, 0.35),
    ) +
    ground(p, 50, 92, 32, 4) +
    `<g transform="rotate(-18 50 50)">` +
    `<path d="M29 50H71" stroke="${edge(steel(P), P)}" stroke-width="4"/>` +
    panel(4) +
    panel(70) +
    `<rect x="36" y="36" width="28" height="28" rx="5" fill="url(#${p}b)" ${ol(g, P)}/>` +
    `<path d="M36 46H64M36 55H64" stroke="${edge(g, P)}" stroke-opacity=".35" stroke-width="1.5"/>` +
    `<path d="M50 36V26" stroke="${edge(steel(P), P)}" stroke-width="2.5"/>` +
    `<path d="M38 26Q50 34 62 26Q58 14 50 14Q42 14 38 26Z" fill="url(#${p}d)" ${ol(steel(P), P)}/>` +
    `<circle cx="50" cy="22" r="2.4" fill="${P.teal}"/>` +
    gloss(43, 42, 6, 2.5, -30, 0.55) +
    `</g>`
  );
};

const astronaut: Draw = (P, p) => {
  const s = pale(P);
  const v = P.dark ? mix(P.teal, P.blue, 0.4) : mix(P.teal, P.blue, 0.3);
  return (
    D(
      drop(P, p),
      body(`${p}b`, s, 0.4, P.dark ? 0.3 : 0.2, 0.5, 1),
      ball(`${p}h`, s, 0.5, P.dark ? 0.35 : 0.22),
      body(`${p}v`, v, 0.25, 0.55, 0.6, 1),
    ) +
    ground(p, 50, 94, 26, 3.5) +
    `<path d="M22 96V80Q22 62 50 62Q78 62 78 80V96Z" ${fl(p, 'b')} ${ol(s, P)}/>` +
    `<rect x="40" y="72" width="20" height="13" rx="3" fill="${P.dark ? mix(P.blue, P.bg, 0.4) : tint(P.blue, 0.75)}" ${ol(P.blue, P, 1.4)}/>` +
    `<circle cx="45" cy="78.5" r="2.2" fill="${P.orange}"/><circle cx="52" cy="78.5" r="2.2" fill="${P.teal}"/><path d="M56 76V81" stroke="${P.red}" stroke-width="2" stroke-linecap="round"/>` +
    `<circle cx="50" cy="38" r="27" fill="url(#${p}h)" ${ol(s, P)}/>` +
    `<rect x="30" y="27" width="40" height="25" rx="12.5" fill="url(#${p}v)" ${ol(v, P)}/>` +
    `<path d="M36 34Q42 29 50 30" fill="none" stroke="#FFFFFF" stroke-opacity=".75" stroke-width="3" stroke-linecap="round"/>` +
    `<circle cx="61" cy="45" r="1.8" fill="#FFFFFF" fill-opacity=".5"/>` +
    gloss(36, 20, 7, 3, -35, 0.6)
  );
};

const ufo: Draw = (P, p) =>
  D(
    drop(P, p),
    ball(`${p}d`, P.teal, 0.5, 0.3),
    body(`${p}u`, P.purple, 0.4, 0.38, 0.3, 1),
    `<linearGradient id="${p}l" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${warm(P)}" stop-opacity=".55"/><stop offset="1" stop-color="${warm(P)}" stop-opacity="0"/></linearGradient>`,
  ) +
  `<path d="M36 62L24 90H76L64 62Z" fill="url(#${p}l)"/>` +
  ground(p, 50, 91, 30, 4) +
  `<g transform="translate(50 52) scale(.74)">` +
  `<path d="M-26 -2C-26 -34 26 -34 26 -2Z" fill="url(#${p}d)" ${ol(P.teal, P, 2.6)}/>` +
  gloss(-10, -18, 8, 4.5, -35, 0.65) +
  `<ellipse cx="0" cy="4" rx="56" ry="16" fill="url(#${p}u)" ${ol(P.purple, P, 2.6)}/>` +
  `<path d="M-50 8Q0 22 50 8" fill="none" stroke="${edge(P.purple, P)}" stroke-width="2" stroke-opacity=".4"/>` +
  `<circle cx="-32" cy="6" r="4.5" fill="${warm(P)}"/><circle cx="0" cy="11" r="4.5" fill="${warm(P)}"/><circle cx="32" cy="6" r="4.5" fill="${warm(P)}"/>` +
  gloss(-22, -3, 20, 3, -4, 0.4) +
  `</g>`;

const blackhole: Draw = (P, p) => {
  const g = warm(P);
  const arc = (front: boolean, w: number, stroke: string, o = 1) =>
    `<path d="M${front ? 88 : 12} 50A38 12 0 0 1 ${front ? 12 : 88} 50" transform="rotate(-20 50 50)" fill="none" stroke="${stroke}" stroke-width="${w}" stroke-opacity="${o}" stroke-linecap="round"/>`;
  return plate(
    P,
    p,
    P.purple,
    'tile',
    D(
      `<linearGradient id="${p}a" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${P.orange}"/><stop offset=".5" stop-color="${tint(g, 0.35)}"/><stop offset="1" stop-color="${P.orange}"/></linearGradient>`,
      `<radialGradient id="${p}h" cx=".5" cy=".5" r=".5"><stop offset="0" stop-color="#05070C"/><stop offset=".72" stop-color="#0A0D18"/><stop offset=".9" stop-color="${shade(P.purple, 0.3)}"/><stop offset="1" stop-color="${tint(P.purple, 0.3)}"/></radialGradient>`,
      fade(`${p}w`, P.purple, 0.9),
    ) +
      `<circle cx="50" cy="50" r="34" fill="url(#${p}w)"/>` +
      arc(false, 9, `url(#${p}a)`, 0.85) +
      `<circle cx="50" cy="50" r="18" fill="url(#${p}h)"/>` +
      `<path d="M38 40A16 16 0 0 1 58 35" fill="none" stroke="${tint(P.purple, 0.5)}" stroke-width="2" stroke-linecap="round" stroke-opacity=".8"/>` +
      arc(true, 11, edge(P.orange, P), 0.6) +
      arc(true, 8, `url(#${p}a)`) +
      arc(true, 2.5, '#FFFFFF', 0.45),
    0.92,
  );
};

const comet: Draw = (P, p) => {
  const c = P.sky;
  return plate(
    P,
    p,
    c,
    'medal',
    D(
      `<linearGradient id="${p}t" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${c}" stop-opacity="0"/><stop offset=".6" stop-color="${c}" stop-opacity=".45"/><stop offset="1" stop-color="${tint(c, 0.5)}" stop-opacity=".95"/></linearGradient>`,
      ball(`${p}b`, tint(c, 0.35), 0.7, 0.25),
    ) +
      `<path d="M10 22L58 50L50 58Z" fill="url(#${p}t)"/>` +
      `<path d="M22 10L62 46L56 52Z" fill="url(#${p}t)" fill-opacity=".7"/>` +
      `<path d="M8 40L52 58L48 62Z" fill="url(#${p}t)" fill-opacity=".6"/>` +
      `<circle cx="62" cy="60" r="15" fill="url(#${p}b)" ${ol(c, P)}/>` +
      gloss(56, 54, 6, 3.2, -40, 0.8) +
      `<polygon points="${star(80, 26, 6, 1.8, 4)}" fill="${warm(P)}"/><polygon points="${star(30, 78, 4.5, 1.3, 4)}" fill="${warm(P)}"/>`,
    0.9,
  );
};

/* ---------------- Sweet Shop ---------------- */

const donut: Draw = (P, p) => {
  const dough = P.dark ? mix(P.gold, P.orange, 0.35) : mix(P.gold, P.orange, 0.2);
  const R = 104;
  const h = 32;
  const hole = `M${-h} -4a${h} ${n(h * 0.86)} 0 1 0 ${2 * h} 0a${h} ${n(h * 0.86)} 0 1 0 ${-2 * h} 0Z`;
  const glaze =
    `M${-R + 8} -4C${-R + 4} -80 -40 ${-R + 4} 0 ${-R + 6}C50 ${-R + 4} ${R - 4} -70 ${R - 8} -6` +
    `Q${R - 2} 18 ${R - 22} 26Q70 58 44 52Q20 76 -6 56Q-40 70 -56 46Q${-R + 4} 46 ${-R + 8} -4Z`;
  const r = rng(3);
  const cols = [P.teal, warm(P), P.blue, P.dark ? pale(P) : P.purple, P.green, P.sky];
  let spr = '';
  for (let i = 0; i < 14; i++) {
    const a = r() * Math.PI * 2;
    const d = h + 18 + r() * (R - h - 38);
    const x = Math.cos(a) * d;
    const y = Math.sin(a) * d * 0.9 - 6;
    spr += `<rect x="${n(x - 8)}" y="${n(y - 3)}" width="16" height="6" rx="3" transform="rotate(${n(r() * 180)} ${n(x)} ${n(y)})" fill="${cols[i % cols.length]}"/>`;
  }
  return (
    D(drop(P, p), ball(`${p}d`, dough, 0.35, 0.42), ball(`${p}g`, P.pink, 0.45, 0.3)) +
    ground(p, 53, 88, 38, 5) +
    `<g transform="translate(50 50) scale(.38)">` +
    `<path d="M${-R} 0a${R} ${n(R * 0.9)} 0 1 0 ${2 * R} 0a${R} ${n(R * 0.9)} 0 1 0 ${-2 * R} 0Z${hole}" fill-rule="evenodd" fill="url(#${p}d)" ${ol(dough, P, 5)}/>` +
    `<path d="${glaze}${hole}" fill-rule="evenodd" fill="url(#${p}g)" ${ol(P.pink, P, 5)}/>` +
    spr +
    `<path d="M${-h + 4} 10Q0 ${h} ${h - 4} 10" fill="none" stroke="${shade(dough, 0.3)}" stroke-width="6" stroke-opacity=".5" stroke-linecap="round"/>` +
    gloss(-46, -52, 30, 10, -30, 0.5) +
    `</g>`
  );
};

const cupcake: Draw = (P, p) => {
  const w = P.teal;
  return (
    D(
      drop(P, p),
      body(`${p}w`, w, 0.4, 0.38, 0.6, 1),
      ball(`${p}f`, P.pink, 0.5, 0.3),
      ball(`${p}c`, P.red, 0.5, 0.4),
    ) +
    ground(p, 50, 93, 26, 4) +
    `<path d="M24 56L32 90H68L76 56Z" fill="url(#${p}w)" ${ol(w, P)}/>` +
    `<path d="M34 58L39 89M44 58L46 89M56 58L54 89M66 58L61 89" stroke="${edge(w, P)}" stroke-opacity=".45" stroke-width="1.8"/>` +
    `<path d="M19 58Q12 48 22 42Q20 30 34 30Q38 20 50 22Q62 20 66 30Q80 30 78 42Q88 48 81 58Z" fill="url(#${p}f)" ${ol(P.pink, P)}/>` +
    `<path d="M24 46Q36 52 50 44Q64 52 76 46M34 34Q44 40 58 32" fill="none" stroke="${edge(P.pink, P)}" stroke-opacity=".35" stroke-width="1.8" stroke-linecap="round"/>` +
    gloss(33, 38, 9, 4, -30, 0.55) +
    `<path d="M52 16Q54 8 60 5" fill="none" stroke="${shade(P.green, 0.2)}" stroke-width="2.4" stroke-linecap="round"/>` +
    `<circle cx="51" cy="18" r="7" fill="url(#${p}c)" ${ol(P.red, P, 1.6)}/>` +
    gloss(48.5, 15.5, 2.4, 1.4, -40, 0.8)
  );
};

const lollipop: Draw = (P, p) => {
  let sp = 'M50 38';
  for (let t = 0; t <= 4.3 * Math.PI; t += 0.35) {
    const rr = 1.5 + t * 1.85;
    sp += `L${n(50 + rr * Math.cos(t))} ${n(38 + rr * Math.sin(t))}`;
  }
  const st = P.dark ? pale(P) : tint(P.blue, 0.85);
  return (
    D(drop(P, p), ball(`${p}b`, P.pink, 0.5, 0.38), body(`${p}k`, st, 0.4, 0.3, 1, 0.2)) +
    ground(p, 50, 94, 16, 3) +
    `<rect x="46.5" y="58" width="7" height="36" rx="3.5" fill="url(#${p}k)" ${ol(st, P, 1.5)}/>` +
    `<circle cx="50" cy="38" r="30" fill="url(#${p}b)" ${ol(P.pink, P)}/>` +
    `<path d="${sp}" fill="none" stroke="#FFFFFF" stroke-opacity=".6" stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round"/>` +
    gloss(38, 24, 9, 4.5, -38, 0.65) +
    `<path d="M42 64L50 60L58 64L55 70L50 66L45 70Z" fill="${P.teal}" ${ol(P.teal, P, 1.4)}/>`
  );
};

const gummy: Draw = (P, p) => {
  const c = P.green;
  return (
    D(drop(P, p), ball(`${p}b`, c, 0.55, 0.35)) +
    ground(p, 50, 94, 24, 3.5) +
    `<g ${fl(p, 'b')} ${ol(c, P)}>` +
    `<circle cx="32" cy="18" r="8"/><circle cx="68" cy="18" r="8"/>` +
    `<ellipse cx="36" cy="88" rx="10" ry="7"/><ellipse cx="64" cy="88" rx="10" ry="7"/>` +
    `<ellipse cx="50" cy="68" rx="21" ry="22"/>` +
    `<ellipse cx="27" cy="62" rx="7" ry="10" transform="rotate(25 27 62)"/><ellipse cx="73" cy="62" rx="7" ry="10" transform="rotate(-25 73 62)"/>` +
    `<circle cx="50" cy="34" r="20"/></g>` +
    `<ellipse cx="50" cy="41" rx="8" ry="5.5" fill="${tint(c, 0.35)}"/>` +
    `<circle cx="42" cy="32" r="2.6" fill="${shade(c, 0.5)}"/><circle cx="58" cy="32" r="2.6" fill="${shade(c, 0.5)}"/>` +
    `<ellipse cx="50" cy="39" rx="3" ry="2" fill="${shade(c, 0.5)}"/>` +
    gloss(42, 23, 7, 3, -30, 0.7) +
    gloss(40, 60, 5, 9, -20, 0.45) +
    gloss(28, 15, 2.5, 1.5, -30, 0.6)
  );
};

const macaron: Draw = (P, p) => {
  const c = P.purple;
  const fill = P.dark ? pale(P) : tint(P.gold, 0.85);
  return (
    D(drop(P, p), body(`${p}b`, c, 0.45, 0.35, 0.4, 1), body(`${p}f`, fill, 0.4, 0.2, 0.4, 1)) +
    ground(p, 50, 86, 38, 5) +
    `<g transform="translate(50 54) scale(.82)">` +
    `<path d="M-44 8H44Q46 32 0 34Q-46 32 -44 8Z" fill="url(#${p}b)" ${ol(c, P, 2.4)}/>` +
    `<path d="M-44 10Q-40 16 -34 12Q-28 18 -20 12Q-12 18 -4 12Q4 18 12 12Q20 18 28 12Q36 18 44 10" fill="none" stroke="${edge(c, P)}" stroke-opacity=".4" stroke-width="2"/>` +
    `<rect x="-46" y="-5" width="92" height="15" rx="7.5" fill="url(#${p}f)" ${ol(fill, P, 2)}/>` +
    `<path d="M-44 -5Q-44 -38 0 -38Q44 -38 44 -5Z" fill="url(#${p}b)" ${ol(c, P, 2.4)}/>` +
    `<path d="M-44 -7Q-40 -1 -34 -5Q-28 1 -20 -5Q-12 1 -4 -5Q4 1 12 -5Q20 1 28 -5Q36 1 44 -7" fill="none" stroke="${edge(c, P)}" stroke-opacity=".4" stroke-width="2"/>` +
    gloss(-18, -26, 17, 5, -10, 0.55) +
    `</g>`
  );
};

/** Candy jar: glass body with sweets, an empty label (value goes on the badge), lid of `lid` colour. */
const jarArt =
  (lid: (P: ArtPal) => string, glow: (P: ArtPal) => string, k = 0.86): Draw =>
  (P, p) => {
    const L = lid(P);
    const glass = P.dark ? P.sky : tint(P.sky, 0.2);
    const label = P.dark ? mix(pale(P), P.gold, 0.12) : '#FFFFFF';
    return plate(
      P,
      p,
      glow(P),
      'medal',
      D(
        drop(P, p),
        body(`${p}l`, L, 0.45, 0.38, 0.4, 1),
        `<linearGradient id="${p}j" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${glass}" stop-opacity=".45"/><stop offset=".3" stop-color="${glass}" stop-opacity=".15"/><stop offset=".8" stop-color="${glass}" stop-opacity=".2"/><stop offset="1" stop-color="${glass}" stop-opacity=".5"/></linearGradient>`,
        ball(`${p}a`, P.pink, 0.45, 0.3),
        ball(`${p}o`, P.orange, 0.45, 0.3),
        ball(`${p}e`, P.green, 0.45, 0.3),
        body(`${p}t`, label, 0.3, 0.12, 0.4, 1),
      ) +
        ground(p, 50, 93, 30, 4) +
        `<circle cx="36" cy="80" r="9" fill="url(#${p}a)"/><circle cx="60" cy="80" r="10" fill="url(#${p}o)"/><circle cx="48" cy="70" r="8" fill="url(#${p}e)"/><circle cx="68" cy="66" r="7" fill="url(#${p}a)"/><circle cx="30" cy="64" r="6" fill="url(#${p}e)"/>` +
        `<path d="M26 30H74Q84 30 84 42V82Q84 92 72 92H28Q16 92 16 82V42Q16 30 26 30Z" fill="url(#${p}j)" stroke="${edge(glass, P)}" stroke-width="2.2"/>` +
        `<rect x="28" y="44" width="44" height="18" rx="4" fill="url(#${p}t)" ${ol(label, P, 1.6)}/>` +
        `<path d="M22 40V82" stroke="#FFFFFF" stroke-opacity=".6" stroke-width="3.5" stroke-linecap="round"/>` +
        `<rect x="22" y="12" width="56" height="18" rx="5" fill="url(#${p}l)" ${ol(L, P)}/>` +
        `<path d="M26 22H74" stroke="${edge(L, P)}" stroke-opacity=".35" stroke-width="2"/>` +
        gloss(34, 16, 9, 2.4, -6, 0.6),
      k,
    );
  };

const jar = jarArt(
  (P) => P.pink,
  (P) => P.pink,
);
const jar_mini = jarArt(
  (P) => P.blue,
  (P) => P.blue,
);
const jar_major = jarArt(
  (P) => P.purple,
  (P) => P.purple,
);
const jar_grand = jarArt(
  (P) => warm(P),
  (P) => P.gold,
  0.88,
);

/* ---------------- Workshop ---------------- */

const gear: Draw = (P, p) => {
  const g = warm(P);
  const hub = P.dark ? P.bg : shade(P.bg, 0.08);
  return (
    D(drop(P, p), ball(`${p}b`, g, 0.45, 0.45)) +
    ground(p, 52, 92, 30, 4.5) +
    `<path d="${gearPath(50, 50, 42, 33, 10)}" fill="url(#${p}b)" ${ol(g, P)}/>` +
    `<circle cx="50" cy="50" r="24" fill="none" stroke="${shade(g, 0.25)}" stroke-opacity=".4" stroke-width="3"/>` +
    [0, 1, 2, 3, 4, 5]
      .map((i) => {
        const a = (i * Math.PI) / 3 + 0.5;
        return `<circle cx="${n(50 + 17 * Math.cos(a))}" cy="${n(50 + 17 * Math.sin(a))}" r="3.6" fill="${hub}" ${ol(g, P, 1.4)}/>`;
      })
      .join('') +
    `<circle cx="50" cy="50" r="9" fill="${hub}" ${ol(g, P)}/>` +
    `<circle cx="50" cy="50" r="4" fill="url(#${p}b)"/>` +
    gloss(36, 30, 11, 4, -38, 0.5)
  );
};

const wrench: Draw = (P, p) => {
  const c = steel(P);
  return (
    D(drop(P, p), body(`${p}b`, c, 0.45, 0.4, 0.3, 1)) +
    ground(p, 50, 90, 34, 4.5) +
    `<g transform="translate(50 50) rotate(-40) scale(.5)">` +
    `<path d="M-70 -10H48V10H-70Z" fill="url(#${p}b)" ${ol(c, P, 4)}/>` +
    `<path d="M40 -24C58 -34 80 -26 86 -9L68 -9L61 0L68 9L86 9C80 26 58 34 40 24C33 15 33 -15 40 -24Z" fill="url(#${p}b)" ${ol(c, P, 4)}/>` +
    `<circle cx="-78" cy="0" r="18" fill="url(#${p}b)" ${ol(c, P, 4)}/>` +
    `<circle cx="-78" cy="0" r="7" fill="${P.dark ? P.bg : shade(P.bg, 0.08)}" ${ol(c, P, 3)}/>` +
    `<rect x="-40" y="-4" width="60" height="8" rx="4" fill="${shade(c, 0.2)}" fill-opacity=".35"/>` +
    gloss(-20, -5, 40, 2.6, 0, 0.6) +
    `</g>`
  );
};

const bulb: Draw = (P, p) => {
  const g = warm(P);
  return (
    D(
      drop(P, p),
      fade(`${p}h`, P.gold, P.dark ? 0.45 : 0.55),
      ball(`${p}b`, tint(g, 0.35), 0.65, 0.2),
      body(`${p}m`, steel(P), 0.4, 0.35, 0.3, 1),
    ) +
    `<circle cx="50" cy="38" r="40" fill="url(#${p}h)"/>` +
    ground(p, 50, 94, 16, 3) +
    `<path d="M50 8C75 8 83 38 65 57Q60 62 60 72H40Q40 62 35 57C17 38 25 8 50 8Z" fill="url(#${p}b)" ${ol(P.gold, P)}/>` +
    `<path d="M42 48q4 -8 8 0t8 0" fill="none" stroke="${P.orange}" stroke-width="2.6" stroke-linecap="round"/>` +
    `<path d="M44 49L46 70M56 49L54 70" stroke="${edge(g, P)}" stroke-opacity=".45" stroke-width="1.4"/>` +
    `<rect x="38" y="71" width="24" height="16" rx="4" fill="url(#${p}m)" ${ol(steel(P), P, 1.6)}/>` +
    `<path d="M38 76.5H62M38 81.5H62" stroke="${edge(steel(P), P)}" stroke-opacity=".5" stroke-width="1.4"/>` +
    `<path d="M45 87H55L53 92H47Z" fill="${edge(steel(P), P)}"/>` +
    gloss(39, 22, 8, 5, -38, 0.75)
  );
};

const blueprint: Draw = (P, p) => {
  const c = P.blue;
  const line = P.dark ? pale(P) : '#FFFFFF';
  return (
    D(drop(P, p), body(`${p}b`, c, 0.35, 0.35, 0.6, 1), body(`${p}q`, shade(c, 0.1), 0.45, 0.35, 1, 0.1)) +
    ground(p, 52, 91, 36, 4.5) +
    `<g transform="rotate(-8 50 52)">` +
    `<rect x="18" y="20" width="70" height="62" rx="3" fill="url(#${p}b)" ${ol(c, P)}/>` +
    `<path d="M28 32H80M28 44H80M28 56H80M28 68H80M40 20V82M54 20V82M68 20V82" stroke="${line}" stroke-opacity=".14" stroke-width="1"/>` +
    `<circle cx="56" cy="51" r="15" fill="none" stroke="${line}" stroke-opacity=".85" stroke-width="2"/>` +
    `<circle cx="56" cy="51" r="5" fill="none" stroke="${line}" stroke-opacity=".85" stroke-width="2"/>` +
    `<path d="M32 30H76M32 27V33M76 27V33M36 72L46 62" stroke="${line}" stroke-opacity=".7" stroke-width="1.6" stroke-linecap="round"/>` +
    `<rect x="10" y="16" width="12" height="70" rx="6" fill="url(#${p}q)" ${ol(c, P)}/>` +
    `<ellipse cx="16" cy="18" rx="6" ry="2.5" fill="${shade(c, 0.35)}"/>` +
    gloss(14, 40, 1.8, 14, 0, 0.5) +
    `</g>`
  );
};

const robot: Draw = (P, p) => {
  const c = P.dark ? mix(P.teal, P.blue, 0.35) : mix(P.teal, P.blue, 0.25);
  const eyeC = P.dark ? P.teal : tint(P.teal, 0.3);
  return plate(
    P,
    p,
    P.teal,
    'tile',
    D(
      drop(P, p),
      body(`${p}b`, c, 0.45, 0.4, 0.4, 1),
      ball(`${p}a`, P.orange, 0.5, 0.3),
      fade(`${p}e`, eyeC, 1),
    ) +
      ground(p, 50, 95, 22, 3.5) +
      `<path d="M50 20V10" stroke="${edge(c, P)}" stroke-width="3" stroke-linecap="round"/>` +
      `<circle cx="50" cy="9" r="5" fill="url(#${p}a)" ${ol(P.orange, P, 1.5)}/>` +
      `<rect x="30" y="66" width="40" height="28" rx="7" fill="url(#${p}b)" ${ol(c, P)}/>` +
      `<circle cx="50" cy="79" r="5" fill="${warm(P)}" ${ol(P.gold, P, 1.4)}/>` +
      `<rect x="13" y="32" width="8" height="18" rx="4" fill="url(#${p}b)" ${ol(c, P)}/><rect x="79" y="32" width="8" height="18" rx="4" fill="url(#${p}b)" ${ol(c, P)}/>` +
      `<rect x="19" y="19" width="62" height="44" rx="13" fill="url(#${p}b)" ${ol(c, P)}/>` +
      `<rect x="26" y="28" width="48" height="24" rx="9" fill="#0A1020" stroke="${edge(c, P)}" stroke-width="1.6"/>` +
      `<circle cx="39" cy="40" r="9" fill="url(#${p}e)"/><circle cx="61" cy="40" r="9" fill="url(#${p}e)"/>` +
      `<circle cx="39" cy="40" r="4.2" fill="${eyeC}"/><circle cx="61" cy="40" r="4.2" fill="${eyeC}"/>` +
      `<path d="M44 47Q50 50 56 47" fill="none" stroke="${eyeC}" stroke-width="1.8" stroke-linecap="round"/>` +
      gloss(32, 24, 10, 3, -12, 0.55),
    0.84,
  );
};

/** battery: an upright cell in the scene green with steel caps and a lightning mark (Workshop, rebalance 2026-09-30) */
const battery: Draw = (P, p) => {
  const c = P.dark ? P.green : shade(P.green, 0.12);
  const m = steel(P);
  const mark = P.dark ? pale(P) : '#FFFFFF';
  return (
    D(drop(P, p), body(`${p}b`, c, 0.45, 0.4, 0.8, 0.2), body(`${p}m`, m, 0.5, 0.4, 0.8, 0.2)) +
    ground(p, 50, 92, 22, 4) +
    `<g transform="rotate(-10 50 52)">` +
    `<rect x="41" y="9" width="18" height="10" rx="3" fill="url(#${p}m)" ${ol(m, P, 1.8)}/>` +
    `<rect x="28" y="17" width="44" height="72" rx="10" fill="url(#${p}b)" ${ol(c, P)}/>` +
    `<path d="M28 31H72V27A10 10 0 0 0 62 17H38A10 10 0 0 0 28 27Z" fill="url(#${p}m)" ${ol(m, P, 1.8)}/>` +
    `<path d="M28 77H72V79A10 10 0 0 1 62 89H38A10 10 0 0 1 28 79Z" fill="url(#${p}m)" ${ol(m, P, 1.8)}/>` +
    `<path d="M54 36L40 57H50L46 72L61 49H51Z" fill="${mark}" fill-opacity=".92" stroke="${edge(c, P)}" stroke-width="1.4" stroke-linejoin="round"/>` +
    gloss(35, 50, 3, 16, 0, 0.5) +
    `</g>`
  );
};

/** hammer: a wooden handle and a steel head with a claw (Workshop, rebalance 2026-09-30) */
const hammer: Draw = (P, p) => {
  const w = wood(P);
  const m = steel(P);
  return (
    D(drop(P, p), body(`${p}w`, tint(w, 0.25), 0.4, 0.4, 0.3, 1), body(`${p}m`, m, 0.5, 0.45, 0.3, 1)) +
    ground(p, 50, 91, 30, 4.5) +
    `<g transform="translate(50 50) rotate(32) scale(.78)">` +
    `<rect x="-7" y="-28" width="14" height="72" rx="7" fill="url(#${p}w)" ${ol(w, P, 2.6)}/>` +
    `<path d="M-7 26H7M-7 33H7" stroke="${edge(w, P)}" stroke-opacity=".45" stroke-width="2"/>` +
    `<path d="M2 -42C14 -42 24 -47 30 -56C36 -42 28 -26 12 -24H2Z" fill="url(#${p}m)" ${ol(m, P, 2.6)}/>` +
    `<rect x="-22" y="-42" width="26" height="18" rx="3" fill="url(#${p}m)" ${ol(m, P, 2.6)}/>` +
    `<rect x="-38" y="-47" width="18" height="28" rx="4" fill="url(#${p}m)" ${ol(m, P, 2.6)}/>` +
    `<path d="M-34 -41V-26M-17 -37H0" stroke="#FFFFFF" stroke-opacity=".5" stroke-width="2.4" stroke-linecap="round"/>` +
    `<path d="M-3 -20V30" stroke="#FFFFFF" stroke-opacity=".3" stroke-width="2.6" stroke-linecap="round"/>` +
    `</g>`
  );
};

/** part: a hex nut on a medal — the collectible for the progress bar (≠ gear: flat faces, no teeth) */
const part: Draw = (P, p) => {
  const c = steel(P);
  const hex = [0, 1, 2, 3, 4, 5]
    .map((i) => `${n(50 + 34 * Math.cos((i * Math.PI) / 3))},${n(50 + 34 * Math.sin((i * Math.PI) / 3))}`)
    .join(' ');
  return plate(
    P,
    p,
    P.orange,
    'medal',
    D(drop(P, p), body(`${p}b`, c, 0.5, 0.45, 0.5, 1), body(`${p}i`, shade(c, 0.35), 0.1, 0.3, 0.3, 1)) +
      ground(p, 52, 90, 30, 4) +
      `<polygon points="${hex}" fill="url(#${p}b)" ${ol(c, P, 2.4)}/>` +
      `<path d="M50 50L84 50M50 50L33 20.6M50 50L33 79.4" stroke="${shade(c, 0.2)}" stroke-opacity=".25" stroke-width="1.5"/>` +
      `<circle cx="50" cy="50" r="22" fill="${tint(c, 0.15)}" ${ol(c, P, 1.6)}/>` +
      `<circle cx="50" cy="50" r="13" fill="url(#${p}i)" ${ol(c, P, 2)}/>` +
      `<path d="M41 45A10 10 0 0 1 55 40.5" fill="none" stroke="#FFFFFF" stroke-opacity=".35" stroke-width="2" stroke-linecap="round"/>` +
      gloss(36, 27, 10, 3.5, -30, 0.6),
    0.84,
  );
};

/* ---------------- Koi Pond ---------------- */

/** koi silhouette (local coords: head left at x −46, tail fin to x 62) — shared by the koi symbol */
const KOI_BODY =
  'M-46 2C-46 -12 -32 -20 -12 -20C8 -20 22 -12 32 -4C34 -1 34 3 32 5C22 13 8 18 -12 18C-32 18 -46 14 -46 2Z';

/** golden koi with red patches, flowing tail and barbels, swimming up-left on a gold tile */
const koiFish = (P: ArtPal, p: string): string => {
  const gold = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.72);
  const fin = P.dark ? P.orange : tint(P.orange, 0.1);
  const rays = `fill="none" stroke="${edge(fin, P)}" stroke-width="1.2" stroke-opacity=".45" stroke-linecap="round"`;
  return (
    D(
      drop(P, p),
      body(`${p}k`, gold, 0.55, 0.28, 0.2, 1),
      body(`${p}f`, fin, 0.45, 0.3, 0.6, 1),
      ball(`${p}x`, P.red, 0.3, 0.3),
      `<clipPath id="${p}c"><path d="${KOI_BODY}"/></clipPath>`,
    ) +
    ground(p, 50, 88, 30, 4) +
    `<g transform="translate(50 54) rotate(-20) scale(.72)">` +
    `<path d="M28 -2C36 -12 44 -22 54 -26C50 -16 49 -8 46 0C49 8 51 16 55 26C44 24 36 14 28 4Z" ${fl(p, 'f')} ${ol(fin, P, 2)}/>` +
    `<path d="M34 -3L48 -18M36 0L44 0M34 3L48 18" ${rays}/>` +
    `<path d="M-16 -18C-10 -34 10 -32 20 -13Z" ${fl(p, 'f')} ${ol(fin, P, 2)}/>` +
    `<path d="M-6 -20L-4 -28M4 -18L6 -26" ${rays}/>` +
    `<path d="${KOI_BODY}" ${fl(p, 'k')} ${ol(gold, P, 2.4)}/>` +
    `<g clip-path="url(#${p}c)" fill="url(#${p}x)">` +
    `<ellipse cx="-24" cy="-16" rx="12" ry="8"/><ellipse cx="8" cy="-14" rx="11" ry="9" transform="rotate(12 8 -14)"/><ellipse cx="22" cy="10" rx="7" ry="6"/></g>` +
    `<path d="M-14 -4q4 4 0 8M-4 -6q4 5 0 10M6 -4q4 4 0 8" fill="none" stroke="${edge(gold, P)}" stroke-width="1.2" stroke-opacity=".4" stroke-linecap="round"/>` +
    `<path d="M-26 10C-30 22 -26 30 -16 32C-16 24 -16 18 -18 11Z" ${fl(p, 'f')} ${ol(fin, P, 1.8)}/>` +
    `<path d="M-45 4q-7 3 -9 11M-44 6q-3 6 -1 12" fill="none" stroke="${edge(gold, P)}" stroke-width="1.6" stroke-linecap="round"/>` +
    eye(-35, -5, 3.8) +
    gloss(-18, -12, 16, 3.5, -4, 0.6) +
    `</g>`
  );
};
const koi: Draw = (P, p) => plate(P, p, P.gold, 'tile', koiFish(P, p), 0.9);

/** moon bridge: full moon behind a red arched bridge whose reflection closes the circle, on a sky medal */
const moonBridge: Draw = (P, p) => {
  const moon = P.dark ? '#FFF1C2' : '#FFE7A3';
  const wood = P.red;
  const arch = 'M10 66C14 26 86 26 90 66H78C74 40 26 40 22 66Z';
  return plate(
    P,
    p,
    P.sky,
    'medal',
    D(
      fade(`${p}mh`, moon, P.dark ? 0.55 : 0.8),
      ball(`${p}mo`, moon, 0.6, 0.18),
      body(`${p}w`, P.dark ? mix(P.blue, P.bg, 0.35) : tint(P.sky, 0.45), 0.25, 0.35, 0, 1),
      body(`${p}b`, wood, 0.4, 0.35, 0.3, 1),
    ) +
      `<circle cx="58" cy="26" r="26" fill="url(#${p}mh)"/>` +
      `<circle cx="58" cy="26" r="15" fill="url(#${p}mo)" stroke="${shade(moon, 0.25)}" stroke-width="1.2"/>` +
      `<ellipse cx="50" cy="74" rx="42" ry="14" fill="url(#${p}w)"/>` +
      `<path d="M22 66C26 94 74 94 78 66" fill="none" stroke="${wood}" stroke-opacity=".45" stroke-width="5" stroke-linecap="round"/>` +
      `<path d="M28 78h10M60 82h12M44 86h8" stroke="#FFFFFF" stroke-opacity="${P.dark ? 0.35 : 0.8}" stroke-width="1.6" stroke-linecap="round"/>` +
      `<path d="M13 50C20 16 80 16 87 50" fill="none" stroke="${edge(wood, P)}" stroke-width="2.6" stroke-linecap="round"/>` +
      `<path d="M19 40V47M30 30V39M42 25V35M58 25V35M70 30V39M81 40V47" stroke="${edge(wood, P)}" stroke-width="2.6" stroke-linecap="round"/>` +
      `<path d="${arch}" ${fl(p, 'b')} ${ol(wood, P, 2.2)}/>` +
      `<path d="M16 58C22 34 78 34 84 58" fill="none" stroke="${tint(wood, 0.45)}" stroke-opacity=".6" stroke-width="1.6" stroke-linecap="round"/>` +
      gloss(34, 38, 8, 2.4, -32, 0.55),
    0.9,
  );
};

/** pink lotus (7 petals) on a pad — never a white flower */
const lotus: Draw = (P, p) => {
  const c = P.pink;
  const petal = (rot: number, s: number, id: string) =>
    `<path d="M0 0C-9 -12 -8 -30 0 -40C8 -30 9 -12 0 0Z" transform="translate(50 64) rotate(${rot}) scale(${s})" ${fl(p, id)} ${ol(c, P, n(1.8 / s))}/>`;
  return (
    D(
      drop(P, p),
      body(`${p}a`, c, 0.45, 0.3, 0.4, 1),
      body(`${p}c`, tint(c, 0.25), 0.55, 0.2, 0.4, 1),
      ball(`${p}pd`, P.green, 0.35, 0.4),
    ) +
    ground(p, 50, 90, 34, 5) +
    `<ellipse cx="50" cy="74" rx="38" ry="12" fill="url(#${p}pd)" ${ol(P.green, P, 2)}/>` +
    `<path d="M50 74L86 70" stroke="${edge(P.green, P)}" stroke-width="1.6" stroke-opacity=".5"/>` +
    petal(-62, 0.9, 'a') +
    petal(62, 0.9, 'a') +
    petal(-32, 1, 'a') +
    petal(32, 1, 'a') +
    petal(-12, 1.05, 'c') +
    petal(12, 1.05, 'c') +
    petal(0, 1.1, 'c') +
    `<circle cx="50" cy="60" r="5" fill="${warm(P)}" ${ol(P.gold, P, 1.2)}/>` +
    gloss(44, 36, 4, 8, -10, 0.5)
  );
};

/** round red lantern with gold caps and a three-strand tassel, soft glow */
const lantern: Draw = (P, p) => {
  const c = P.red;
  const g = warm(P);
  return (
    D(
      drop(P, p),
      ball(`${p}b`, c, 0.5, 0.4),
      body(`${p}g`, g, 0.5, 0.35, 1, 0),
      fade(`${p}h`, P.orange, P.dark ? 0.5 : 0.35),
    ) +
    `<circle cx="50" cy="52" r="42" fill="url(#${p}h)"/>` +
    ground(p, 50, 94, 20, 3.5) +
    `<path d="M50 6V14" stroke="${edge(g, P)}" stroke-width="2.4" stroke-linecap="round"/>` +
    `<rect x="36" y="14" width="28" height="9" rx="3" fill="url(#${p}g)" ${ol(g, P, 1.8)}/>` +
    `<ellipse cx="50" cy="50" rx="33" ry="28" fill="url(#${p}b)" ${ol(c, P, 2.2)}/>` +
    `<path d="M50 22V78M50 22C38 30 38 70 50 78M50 22C62 30 62 70 50 78M50 22C26 30 24 70 50 78M50 22C74 30 76 70 50 78" fill="none" stroke="${shade(c, 0.3)}" stroke-width="1.4" stroke-opacity=".55"/>` +
    `<rect x="36" y="76" width="28" height="8" rx="3" fill="url(#${p}g)" ${ol(g, P, 1.8)}/>` +
    `<path d="M50 84V94M46 86L44 95M54 86L56 95" stroke="${g}" stroke-width="2" stroke-linecap="round"/>` +
    gloss(38, 36, 9, 5, -35, 0.55)
  );
};

/** mandarin (桔 ~ 吉) with two leaves */
const mandarin: Draw = (P, p) =>
  D(drop(P, p), ball(`${p}b`, P.orange, 0.45, 0.4), leafDef(P, p)) +
  ground(p, 52, 91, 28, 5) +
  `<circle cx="50" cy="58" r="31" ${fl(p, 'b')} ${ol(P.orange, P)}/>` +
  `<circle cx="50" cy="30" r="3" fill="${shade(P.green, 0.3)}"/>` +
  leaf(p, P, 50, 30, -60, 0.55) +
  leaf(p, P, 52, 30, 50, 0.5) +
  `<g fill="${shade(P.orange, 0.25)}" fill-opacity=".35"><circle cx="38" cy="62" r="1.2"/><circle cx="60" cy="70" r="1.2"/><circle cx="64" cy="52" r="1.2"/><circle cx="46" cy="76" r="1.2"/><circle cx="56" cy="44" r="1.2"/></g>` +
  gloss(38, 46, 9, 5, -40, 0.6);

/** red-and-gold folding fan with 7 ribs and a wave pattern */
const fan: Draw = (P, p) => {
  const c = P.red;
  const g = warm(P);
  const ribs = [-60, -40, -20, 0, 20, 40, 60]
    .map((a) => {
      const r = (a * Math.PI) / 180;
      return `M50 78L${n(50 + 40 * Math.sin(r))} ${n(78 - 40 * Math.cos(r))}`;
    })
    .join('');
  const pt = (a: number) =>
    `${n(50 + 40 * Math.sin((a * Math.PI) / 180))} ${n(78 - 40 * Math.cos((a * Math.PI) / 180))}`;
  const pleats = [-60, -20, 20].map((a) => `M50 78L${pt(a)}A40 40 0 0 1 ${pt(a + 20)}Z`).join('');
  return (
    D(drop(P, p), body(`${p}b`, c, 0.45, 0.3, 0.3, 1), body(`${p}g`, g, 0.5, 0.35, 0.5, 1)) +
    ground(p, 50, 90, 26, 4) +
    `<path d="M50 78L15.4 58A40 40 0 0 1 84.6 58Z" ${fl(p, 'b')} ${ol(c, P, 2.2)}/>` +
    `<path d="${pleats}" fill="${shade(c, 0.35)}" fill-opacity=".28"/>` +
    `<path d="M25.8 64A28 28 0 0 1 74.2 64" fill="none" stroke="${g}" stroke-width="3" stroke-opacity=".8"/>` +
    `<path d="${ribs}" stroke="${shade(c, 0.35)}" stroke-width="1.4" stroke-opacity=".55"/>` +
    `<path d="M34 50q4 -4 8 0t8 0t8 0t8 0" fill="none" stroke="${tint(g, 0.2)}" stroke-width="1.8" stroke-linecap="round" stroke-opacity=".75"/>` +
    `<circle cx="50" cy="78" r="5" fill="url(#${p}g)" ${ol(g, P, 1.6)}/>` +
    `<path d="M50 83V92" stroke="${g}" stroke-width="2.4" stroke-linecap="round"/>` +
    gloss(36, 46, 8, 3, -30, 0.5)
  );
};

/** three bamboo stalks with nodes and leaves */
const bamboo: Draw = (P, p) => {
  const c = P.green;
  const stalk = (x: number, top: number, w: number) =>
    `<rect x="${n(x - w / 2)}" y="${top}" width="${w}" height="${88 - top}" rx="${n(w / 2)}" ${fl(p, 'b')} ${ol(c, P, 1.8)}/>` +
    [top + 18, top + 40, top + 62]
      .filter((y) => y < 84)
      .map(
        (y) => `<path d="M${n(x - w / 2)} ${y}H${n(x + w / 2)}" stroke="${edge(c, P)}" stroke-width="2.2"/>`,
      )
      .join('');
  return (
    D(drop(P, p), body(`${p}b`, c, 0.45, 0.3, 1, 0), leafDef(P, p)) +
    ground(p, 50, 91, 30, 5) +
    stalk(34, 22, 11) +
    stalk(52, 10, 12) +
    stalk(68, 30, 10) +
    leaf(p, P, 52, 28, 55, 0.55) +
    leaf(p, P, 34, 40, -50, 0.5) +
    leaf(p, P, 68, 46, 40, 0.45) +
    gloss(49, 26, 2.5, 10, 0, 0.45)
  );
};

/** round jade coin with a square hole, carved rim and a red cord with a tassel */
const jadeCoin: Draw = (P, p) => {
  const c = mix(P.green, P.gold, P.dark ? 0.12 : 0.08);
  const cord = P.red;
  return (
    D(drop(P, p), ball(`${p}b`, c, 0.5, 0.4), body(`${p}t`, cord, 0.35, 0.35, 0.6, 1)) +
    ground(p, 50, 93, 26, 4) +
    `<circle cx="50" cy="44" r="33" ${fl(p, 'b')} ${ol(c, P, 2.4)}/>` +
    `<circle cx="50" cy="44" r="26" fill="none" stroke="${tint(c, 0.45)}" stroke-opacity=".6" stroke-width="2"/>` +
    `<path d="M50 17q5 3 0 6q-5 3 0 6M50 59q5 3 0 6q-5 3 0 6M23 44q3 -5 6 0q3 5 6 0M65 44q3 -5 6 0q3 5 6 0" fill="none" stroke="${shade(c, 0.35)}" stroke-width="1.5" stroke-opacity=".45" stroke-linecap="round"/>` +
    `<rect x="41" y="35" width="18" height="18" rx="2" fill="${P.dark ? P.bg : shade(P.bg, 0.08)}" ${ol(c, P, 2.4)}/>` +
    `<path d="M50 35V53" stroke="${cord}" stroke-width="3.2" stroke-linecap="round"/>` +
    `<path d="M50 53V70" stroke="${edge(cord, P)}" stroke-width="4" stroke-linecap="round"/><path d="M50 53V70" stroke="${cord}" stroke-width="2.4" stroke-linecap="round"/>` +
    `<path d="M50 68L44 72L50 76L56 72Z" fill="url(#${p}t)" ${ol(cord, P, 1.4)}/>` +
    `<path d="M47 76L44 91H56L53 76Z" fill="url(#${p}t)" ${ol(cord, P, 1.4)}/>` +
    `<path d="M48 80L47 90M52 80L53 90" stroke="${shade(cord, 0.3)}" stroke-width="1" stroke-opacity=".5"/>` +
    gloss(36, 28, 10, 4, -35, 0.6)
  );
};

/** carved rank signs 十 / 九 / 八 (graphics of a rank, owner decision 2026-09-29) — paths, never <text> */
export const RANK_GLYPH = {
  rank10: 'M30 50H70M50 28V74',
  rank9: 'M47 28C47 50 41 64 29 74M31 44H60C60 58 62 68 70 72C73 73 75 72 77 69',
  rank8: 'M45 31C43 50 37 62 27 71M56 33C60 50 66 61 76 70',
} as const;

/** jade medallion with a carved sign: dark groove + light lower edge */
const rank =
  (name: keyof typeof RANK_GLYPH, tone: (P: ArtPal) => string): Draw =>
  (P, p) => {
    const c = mix(tone(P), P.green, 0.1);
    const d = RANK_GLYPH[name];
    return (
      D(drop(P, p), ball(`${p}b`, c, 0.5, 0.4)) +
      ground(p, 50, 91, 28, 5) +
      `<circle cx="50" cy="50" r="38" ${fl(p, 'b')} ${ol(c, P, 2.4)}/>` +
      `<circle cx="50" cy="50" r="31" fill="none" stroke="${tint(c, 0.5)}" stroke-opacity=".55" stroke-width="1.6"/>` +
      `<path d="${d}" fill="none" stroke="${shade(c, 0.55)}" stroke-width="8" stroke-linecap="round" stroke-linejoin="round"/>` +
      `<path d="${d}" transform="translate(.8 1.2)" fill="none" stroke="${tint(c, 0.7)}" stroke-opacity=".35" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>` +
      gloss(36, 30, 12, 5, -35, 0.55)
    );
  };
const rank10 = rank('rank10', (P) => P.teal);
const rank9 = rank('rank9', (P) => P.sky);
const rank8 = rank('rank8', (P) => P.purple);

/* ---------------- Lucky Lever (招财机): a lucky-goods shop, no casino props ---------------- */

/** longevity peach: pointed tip, a soft crease, a pink blush, two leaves on a twig */
const llPeach: Draw = (P, p) => {
  const c = P.dark ? mix(P.pink, P.orange, 0.35) : mix(tint(P.pink, 0.15), P.orange, 0.3);
  const blush = P.dark ? P.red : mix(P.red, P.pink, 0.4);
  const fruit = 'M50 20C42 30 17 28 17 55C17 77 33 89 50 89C67 89 83 77 83 55C83 28 58 30 50 20Z';
  return (
    D(
      drop(P, p),
      ball(`${p}b`, c, 0.55, 0.32),
      fade(`${p}h`, blush, P.dark ? 0.55 : 0.45),
      leafDef(P, p),
      body(`${p}w`, wood(P), 0.3, 0.3, 1, 0.2),
      `<clipPath id="${p}c"><path d="${fruit}"/></clipPath>`,
    ) +
    ground(p, 50, 92, 30, 4.5) +
    `<path d="${fruit}" ${fl(p, 'b')} ${ol(c, P, 2.2)}/>` +
    `<g clip-path="url(#${p}c)"><ellipse cx="66" cy="66" rx="26" ry="24" fill="url(#${p}h)"/></g>` +
    `<path d="M50 23C45 40 45 62 53 86" fill="none" stroke="${edge(c, P)}" stroke-width="2" stroke-opacity=".5" stroke-linecap="round"/>` +
    `<path d="M50 20C48 14 44 11 38 10" fill="none" stroke="url(#${p}w)" stroke-width="3.2" stroke-linecap="round"/>` +
    leaf(p, P, 46, 16, -72, 0.6) +
    leaf(p, P, 50, 18, 58, 0.5) +
    gloss(32, 46, 9, 5, -40, 0.6)
  );
};

/** bottle gourd 葫芦: two bulbs, a stem with a leaf, a red cord tied at the waist */
const llGourd: Draw = (P, p) => {
  const c = P.dark ? mix(P.gold, P.orange, 0.25) : mix(P.gold, '#FFD166', 0.35);
  const cord = P.red;
  const shape =
    'M50 18C59 18 64 25 64 33C64 40 59 43 58 47C70 51 78 60 78 71C78 84 66 92 50 92C34 92 22 84 22 71C22 60 30 51 42 47C41 43 36 40 36 33C36 25 41 18 50 18Z';
  return (
    D(
      drop(P, p),
      ball(`${p}b`, c, 0.5, 0.4),
      leafDef(P, p),
      body(`${p}w`, wood(P), 0.3, 0.3, 1, 0.2),
      body(`${p}t`, cord, 0.35, 0.35, 0.6, 1),
    ) +
    ground(p, 50, 94, 26, 4) +
    `<path d="M50 19V9" stroke="url(#${p}w)" stroke-width="4" stroke-linecap="round"/>` +
    leaf(p, P, 51, 12, 62, 0.42) +
    `<path d="${shape}" ${fl(p, 'b')} ${ol(c, P, 2.2)}/>` +
    `<path d="M41 47Q50 51 59 47" fill="none" stroke="${edge(cord, P)}" stroke-width="5" stroke-linecap="round"/>` +
    `<path d="M41 47Q50 51 59 47" fill="none" stroke="${cord}" stroke-width="3" stroke-linecap="round"/>` +
    `<path d="M58 48C66 50 68 56 64 60C62 56 60 52 58 48ZM58 48C64 46 70 47 71 52C66 52 62 50 58 48Z" fill="url(#${p}t)" ${ol(cord, P, 1.2)}/>` +
    `<path d="M64 60L62 72M65 60L68 71" stroke="${cord}" stroke-width="2" stroke-linecap="round"/>` +
    gloss(43, 27, 4, 6, -20, 0.6) +
    gloss(34, 64, 7, 9, -30, 0.5)
  );
};

/** palace lantern: a red drum with gold ribs and caps, a hook and a gold tassel (not the round koi-pond lantern) */
const llLantern: Draw = (P, p) => {
  const c = P.red;
  const g = warm(P);
  return (
    D(
      drop(P, p),
      `<linearGradient id="${p}b" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${shade(c, 0.35)}"/><stop offset=".35" stop-color="${tint(c, 0.3)}"/><stop offset=".6" stop-color="${c}"/><stop offset="1" stop-color="${shade(c, 0.4)}"/></linearGradient>`,
      body(`${p}g`, g, 0.5, 0.35, 1, 0),
      fade(`${p}h`, P.orange, P.dark ? 0.55 : 0.35),
    ) +
    `<circle cx="50" cy="50" r="44" fill="url(#${p}h)"/>` +
    ground(p, 50, 95, 18, 3) +
    `<path d="M50 4V12" stroke="${edge(g, P)}" stroke-width="2.4" stroke-linecap="round"/>` +
    `<path d="M36 12H64L70 21H30Z" fill="url(#${p}g)" ${ol(g, P, 1.8)}/>` +
    `<path d="M30 21H70C75 34 75 60 70 72H30C25 60 25 34 30 21Z" fill="url(#${p}b)" ${ol(c, P, 2.2)}/>` +
    `<path d="M41 21C39 36 39 58 41 72M59 21C61 36 61 58 59 72" fill="none" stroke="${g}" stroke-width="2.2"/>` +
    `<path d="M29 33H71M29 60H71" stroke="${shade(c, 0.3)}" stroke-width="1.2" stroke-opacity=".55"/>` +
    `<path d="M30 72H70L64 81H36Z" fill="url(#${p}g)" ${ol(g, P, 1.8)}/>` +
    `<circle cx="50" cy="84" r="3" fill="${g}" ${ol(g, P, 1)}/>` +
    `<path d="M50 87V96M47 87L45 96M53 87L55 96" stroke="${g}" stroke-width="2" stroke-linecap="round"/>` +
    gloss(36, 34, 3, 12, -6, 0.45)
  );
};

/** red-and-white koi leaping in an arc over a small splash (no plate: the koi of Koi Pond is the gold wild) */
const llKoi: Draw = (P, p) => {
  const w = P.dark ? '#F1F3F8' : '#FFFFFF';
  const red = P.red;
  const fin = P.dark ? tint(P.orange, 0.1) : P.orange;
  const water = P.sky;
  const fish =
    'M-34 2C-34 -10 -22 -17 -6 -17C10 -17 22 -10 30 -3C33 -1 33 3 30 5C22 12 10 16 -6 16C-22 16 -34 12 -34 2Z';
  return (
    D(
      drop(P, p),
      body(`${p}k`, w, 0.2, 0.18, 0.3, 1),
      body(`${p}rd`, red, 0.35, 0.3, 0.3, 1),
      body(`${p}f`, fin, 0.45, 0.3, 0.6, 1),
      `<clipPath id="${p}c"><path d="${fish}"/></clipPath>`,
    ) +
    ground(p, 50, 92, 26, 4) +
    `<ellipse cx="50" cy="84" rx="30" ry="6" fill="none" stroke="${water}" stroke-opacity=".7" stroke-width="2.4"/>` +
    `<path d="M30 82Q26 70 22 74M70 82Q76 70 80 75M42 80Q40 72 36 70" fill="none" stroke="${water}" stroke-width="2.4" stroke-linecap="round"/>` +
    `<circle cx="22" cy="66" r="2.4" fill="${water}"/><circle cx="80" cy="67" r="2" fill="${water}"/><circle cx="34" cy="62" r="1.6" fill="${water}"/>` +
    `<g transform="translate(45 50) rotate(-30) scale(.84)">` +
    `<path d="M27 -2C36 -14 46 -22 56 -24C52 -14 50 -6 47 0C50 7 53 16 57 25C46 23 36 14 27 4Z" fill="url(#${p}f)" ${ol(fin, P, 1.6)}/>` +
    `<path d="M-12 -16C-6 -29 10 -27 18 -11Z" fill="url(#${p}f)" ${ol(fin, P, 1.6)}/>` +
    `<path d="${fish}" fill="url(#${p}k)" ${ol(w, P, 2.2)}/>` +
    `<g clip-path="url(#${p}c)" fill="url(#${p}rd)"><ellipse cx="-18" cy="-13" rx="12" ry="8"/><ellipse cx="10" cy="-10" rx="12" ry="9"/><ellipse cx="22" cy="10" rx="6" ry="5"/></g>` +
    `<path d="M-20 9C-24 19 -20 26 -12 27C-12 20 -12 15 -14 9Z" fill="url(#${p}f)" ${ol(fin, P, 1.4)}/>` +
    `<path d="M-33 4q-6 3 -7 9" fill="none" stroke="${edge(w, P)}" stroke-width="1.4" stroke-linecap="round"/>` +
    eye(-25, -4, 3.4) +
    gloss(-8, -10, 13, 3, -4, 0.6) +
    `</g>`
  );
};

/** jade plaque pendant: a carved ruyi-cloud tablet on a red cord with a knot and a tassel (not the round jade coin) */
const llJade: Draw = (P, p) => {
  const c = mix(P.green, P.teal, P.dark ? 0.35 : 0.25);
  const cord = P.red;
  const plaque =
    'M30 30Q30 26 34 26H40Q44 20 50 20Q56 20 60 26H66Q70 26 70 30V70Q70 76 64 76H36Q30 76 30 70Z';
  const carve = `fill="none" stroke="${shade(c, 0.4)}" stroke-width="2.4" stroke-opacity=".7" stroke-linecap="round"`;
  return (
    D(drop(P, p), body(`${p}b`, c, 0.5, 0.35, 0.5, 1), body(`${p}t`, cord, 0.35, 0.35, 0.6, 1)) +
    ground(p, 50, 95, 22, 3.5) +
    `<path d="M50 4V20" stroke="${edge(cord, P)}" stroke-width="4" stroke-linecap="round"/><path d="M50 4V20" stroke="${cord}" stroke-width="2.4" stroke-linecap="round"/>` +
    `<path d="M50 9L45 13L50 17L55 13Z" fill="url(#${p}t)" ${ol(cord, P, 1.2)}/>` +
    `<path d="${plaque}" ${fl(p, 'b')} ${ol(c, P, 2.4)}/>` +
    `<path d="M35 33H65V68Q65 71 62 71H38Q35 71 35 68Z" fill="none" stroke="${tint(c, 0.45)}" stroke-opacity=".6" stroke-width="1.6"/>` +
    `<circle cx="50" cy="27" r="2.6" fill="${P.dark ? P.bg : shade(P.bg, 0.08)}" ${ol(c, P, 1.2)}/>` +
    `<path d="M39 60C39 50 51 48 51 56C51 61 45 61 45 57M61 44C61 54 49 56 49 48C49 43 55 43 55 47M38 40H44M56 66H62" ${carve}/>` +
    `<path d="M50 76V82" stroke="${cord}" stroke-width="2.6" stroke-linecap="round"/>` +
    `<path d="M47 82L44 96H56L53 82Z" fill="url(#${p}t)" ${ol(cord, P, 1.3)}/>` +
    `<path d="M48 86L47 95M52 86L53 95" stroke="${shade(cord, 0.3)}" stroke-width="1" stroke-opacity=".5"/>` +
    gloss(38, 34, 4, 10, -12, 0.5)
  );
};

/** gold ingot 元宝 (the special symbol) on a gold medal: raised wings, a domed top, two sparkles */
const llIngot: Draw = (P, p) => {
  const g = warm(P);
  return plate(
    P,
    p,
    P.gold,
    'medal',
    D(body(`${p}a`, g, 0.55, 0.38, 0.5, 1), ball(`${p}d`, g, 0.6, 0.35)) +
      `<path d="M10 42C20 44 30 50 50 50C70 50 80 44 90 42C88 48 84 52 78 54H22C16 52 12 48 10 42Z" fill="url(#${p}a)" ${ol(g, P, 2)}/>` +
      `<ellipse cx="50" cy="48" rx="20" ry="16" fill="url(#${p}d)" ${ol(g, P, 2)}/>` +
      `<path d="M14 50C24 54 34 58 50 58C66 58 76 54 86 50C84 68 70 80 50 80C30 80 16 68 14 50Z" fill="url(#${p}a)" ${ol(g, P, 2.2)}/>` +
      `<path d="M22 60Q50 70 78 60" fill="none" stroke="${tint(g, 0.55)}" stroke-opacity=".7" stroke-width="2" stroke-linecap="round"/>` +
      gloss(43, 40, 7, 4, -30, 0.75) +
      gloss(30, 62, 7, 2.5, -10, 0.5) +
      `<polygon points="${star(78, 26, 7, 2, 4)}" fill="#FFFFFF" fill-opacity=".9"/>` +
      `<polygon points="${star(22, 30, 4.5, 1.4, 4)}" fill="#FFFFFF" fill-opacity=".75"/>`,
    0.92,
  );
};

/** dragon (the wild, middle reel only): a front-facing gold dragon head with antlers and whiskers on a red tile */
const llDragon: Draw = (P, p) => {
  const g = warm(P);
  const mane = P.dark ? P.orange : tint(P.orange, 0.05);
  const horn = P.dark ? '#F4E7C8' : '#FFF4D6';
  const snout = tint(g, P.dark ? 0.35 : 0.45);
  const antlers = 'M38 28C34 20 30 14 24 10M32 18L36 10M62 28C66 20 70 14 76 10M68 18L64 10';
  return plate(
    P,
    p,
    P.red,
    'tile',
    D(
      body(`${p}h`, g, 0.45, 0.38, 0.5, 1),
      body(`${p}mn`, mane, 0.4, 0.35, 0.5, 1),
      body(`${p}n`, horn, 0.3, 0.25, 0.5, 1),
      ball(`${p}s`, snout, 0.4, 0.25),
    ) +
      // mane flames behind the head
      `<path d="M50 14C40 18 30 14 24 22C14 24 12 36 16 44C8 52 12 64 20 66C18 76 28 82 34 80L66 80C72 82 82 76 80 66C88 64 92 52 84 44C88 36 86 24 76 22C70 14 60 18 50 14Z" fill="url(#${p}mn)" ${ol(mane, P, 1.8)}/>` +
      `<path d="${antlers}" fill="none" stroke="${edge(horn, P)}" stroke-width="6" stroke-linecap="round"/>` +
      `<path d="${antlers}" fill="none" stroke="url(#${p}n)" stroke-width="3.6" stroke-linecap="round"/>` +
      `<path d="M50 24C64 24 72 32 72 44C72 50 69 54 70 60C71 70 62 78 50 78C38 78 29 70 30 60C31 54 28 50 28 44C28 32 36 24 50 24Z" fill="url(#${p}h)" ${ol(g, P, 2.2)}/>` +
      `<path d="M34 38Q40 32 47 38M53 38Q60 32 66 38" fill="none" stroke="${edge(mane, P)}" stroke-width="3.4" stroke-linecap="round"/>` +
      eye(41, 44, 4.4) +
      eye(61, 44, 4.4) +
      `<ellipse cx="50" cy="62" rx="15" ry="10" fill="url(#${p}s)" ${ol(g, P, 1.8)}/>` +
      `<path d="M44 59q2 -3 4 0M52 59q2 -3 4 0" fill="none" stroke="${INK}" stroke-width="1.8" stroke-linecap="round"/>` +
      `<path d="M38 70Q50 76 62 70" fill="none" stroke="${INK}" stroke-width="2" stroke-linecap="round"/>` +
      `<path d="M42 71L44 76L46 72ZM58 71L56 76L54 72Z" fill="#FFFFFF" stroke="${INK}" stroke-width=".8" stroke-linejoin="round"/>` +
      // whiskers and beard
      `<path d="M36 62C26 62 18 68 14 78C12 82 16 84 18 80M64 62C74 62 82 68 86 78C88 82 84 84 82 80" fill="none" stroke="${g}" stroke-width="2.4" stroke-linecap="round"/>` +
      `<path d="M44 78Q46 86 42 90M56 78Q54 86 58 90M50 78V88" fill="none" stroke="${mane}" stroke-width="2.4" stroke-linecap="round"/>` +
      gloss(40, 30, 8, 3, -18, 0.55),
    0.9,
  );
};

/* ---------------- Pirate Cove (海盗港): a sunny friendly cove, no casino props, no skulls or weapons ---------------- */

/** treasure chest (the wild): a plank chest with brass bands, the domed lid lifted on a glow of pearls and gems */
const pcChest: Draw = (P, p) => {
  const w = wood(P);
  const b = warm(P);
  const glow = P.dark ? '#FFE9A8' : '#FFE7A3';
  const lid = 'M15 42C15 26 30 17 50 17C70 17 85 26 85 42Z';
  const band = (y: number) =>
    `<rect x="13" y="${y}" width="74" height="6" rx="2" fill="url(#${p}b)" ${ol(b, P, 1.4)}/>`;
  const rivets = [20, 36, 64, 80]
    .map((x) => `<circle cx="${x}" cy="81" r="1.6" fill="${tint(b, 0.4)}"/>`)
    .join('');
  return plate(
    P,
    p,
    P.teal,
    'tile',
    D(
      body(`${p}w`, w, 0.3, 0.4, 0.5, 1),
      body(`${p}l`, tint(w, 0.1), 0.4, 0.35, 0.4, 1),
      body(`${p}b`, b, 0.55, 0.4, 0.5, 1),
      fade(`${p}h`, glow, 0.95),
      ball(`${p}pl`, pale(P), 0.6, 0.3),
      `<clipPath id="${p}c"><path d="${lid}"/></clipPath>`,
    ) +
      `<ellipse cx="54" cy="42" rx="44" ry="28" fill="url(#${p}h)"/>` +
      `<path d="M32 28L26 14M52 26L54 10M70 28L80 14" stroke="${glow}" stroke-opacity=".55" stroke-width="5" stroke-linecap="round"/>` +
      `<path d="M17 53L18 42L84 32L83 53Z" fill="${glow}"/>` +
      `<circle cx="28" cy="48" r="5.5" fill="url(#${p}pl)" ${ol(pale(P), P, 1)}/>` +
      `<circle cx="38" cy="46" r="4.5" fill="url(#${p}pl)" ${ol(pale(P), P, 1)}/>` +
      `<path d="M60 34L67 41L60 50L53 41Z" fill="${P.red}" ${ol(P.red, P, 1.2)}/>` +
      `<path d="M74 39L80 44L75 51L69 46Z" fill="${P.teal}" ${ol(P.teal, P, 1.2)}/>` +
      `<path d="M44 49L49 42L54 49Z" fill="${P.sky}" ${ol(P.sky, P, 1)}/>` +
      `<rect x="15" y="52" width="70" height="34" rx="4" ${fl(p, 'w')} ${ol(w, P, 2)}/>` +
      `<path d="M16 66H84M16 76H84" stroke="${shade(w, 0.35)}" stroke-width="1.4" stroke-opacity=".6"/>` +
      `<g transform="rotate(-9 15 44)"><path d="${lid}" ${fl(p, 'l')} ${ol(w, P, 2)}/>` +
      `<g clip-path="url(#${p}c)" fill="url(#${p}b)"><rect x="24" y="10" width="7" height="34"/><rect x="69" y="10" width="7" height="34"/></g>` +
      `<path d="M17 31Q50 22 83 31" fill="none" stroke="${shade(w, 0.35)}" stroke-width="1.4" stroke-opacity=".6"/>` +
      band(38) +
      gloss(34, 26, 12, 3.5, -12, 0.5) +
      `</g>` +
      band(52) +
      `<rect x="24" y="58" width="7" height="28" fill="url(#${p}b)"/><rect x="69" y="58" width="7" height="28" fill="url(#${p}b)"/>` +
      `<rect x="42" y="54" width="16" height="17" rx="3" fill="url(#${p}b)" ${ol(b, P, 1.4)}/>` +
      `<circle cx="50" cy="60" r="2.6" fill="${INK}"/><path d="M50 61L48 67H52Z" fill="${INK}"/>` +
      rivets +
      `<polygon points="${star(84, 18, 6, 1.8, 4)}" fill="#FFFFFF" fill-opacity=".9"/>` +
      `<polygon points="${star(16, 22, 4, 1.2, 4)}" fill="#FFFFFF" fill-opacity=".75"/>`,
    0.9,
  );
};

/** treasure map (the top symbol) on a sea-blue medal: a parchment with rolled ends, an island, a dashed route to an X */
const pcMap: Draw = (P, p) => {
  const c = P.dark ? '#F1E2BC' : '#FFF1D0';
  const ink = shade(mix(P.orange, P.gold, 0.4), 0.55);
  const sheet = 'M24 22Q50 28 76 22V76Q50 70 24 76Z';
  const roll = (x: number) =>
    `<rect x="${x}" y="18" width="10" height="62" rx="5" fill="url(#${p}r2)" ${ol(c, P, 1.6)}/>`;
  return plate(
    P,
    p,
    P.sky,
    'medal',
    D(
      body(`${p}a`, c, 0.4, 0.22, 0.6, 1),
      `<linearGradient id="${p}r2" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stop-color="${shade(c, 0.25)}"/><stop offset=".45" stop-color="${tint(c, 0.5)}"/><stop offset="1" stop-color="${shade(c, 0.3)}"/></linearGradient>`,
      body(`${p}i`, P.green, 0.35, 0.3, 0.5, 1),
    ) +
      `<path d="${sheet}" ${fl(p, 'a')} ${ol(c, P, 1.8)}/>` +
      `<path d="M28 34Q36 30 42 36M30 64Q38 60 44 66M58 30Q64 26 70 31" fill="none" stroke="${P.sky}" stroke-opacity=".55" stroke-width="1.6" stroke-linecap="round"/>` +
      `<path d="M52 62C52 52 60 46 68 50C74 52 74 60 70 64C66 68 54 68 52 62Z" fill="${tint(mix(P.gold, '#FFD166', 0.5), 0.35)}" ${ol(P.gold, P, 1.4)}/>` +
      `<path d="M56 60C56 54 62 50 66 54C68 57 66 61 62 62C59 63 56 63 56 60Z" fill="url(#${p}i)"/>` +
      `<path d="M31 42C38 38 42 50 48 48C54 46 52 38 58 42C62 45 60 52 62 55" fill="none" stroke="${P.red}" stroke-width="2.4" stroke-dasharray="3 3" stroke-linecap="round"/>` +
      `<path d="M57 51L67 61M67 51L57 61" stroke="${edge(P.red, P)}" stroke-width="6.4" stroke-linecap="round"/>` +
      `<path d="M57 51L67 61M67 51L57 61" stroke="${P.red}" stroke-width="4.2" stroke-linecap="round"/>` +
      `<polygon points="${star(34, 60, 7, 1.8, 4)}" fill="${ink}" fill-opacity=".55"/>` +
      roll(17) +
      roll(73) +
      gloss(36, 28, 10, 2.5, -8, 0.6),
    0.86,
  );
};

/** cheerful parrot on a perch: red body, sun-yellow and sky-blue wing, a white face patch and a hooked beak */
const pcParrot: Draw = (P, p) => {
  const r = P.red;
  const y = P.dark ? P.gold : mix(P.gold, '#FFD166', 0.6);
  const bl = P.dark ? P.sky : P.blue;
  const beak = P.dark ? '#F3E9D2' : '#FFF4DE';
  return (
    D(
      drop(P, p),
      body(`${p}b`, r, 0.35, 0.35, 0.6, 1),
      body(`${p}y`, y, 0.4, 0.3, 0.6, 1),
      body(`${p}u`, bl, 0.35, 0.35, 0.6, 1),
      body(`${p}k`, beak, 0.3, 0.3, 0.5, 1),
      body(`${p}w`, wood(P), 0.3, 0.3, 1, 0.2),
    ) +
    ground(p, 50, 93, 30, 4) +
    `<path d="M42 70C38 80 34 88 30 96L40 95C42 88 46 80 50 72Z" fill="url(#${p}u)" ${ol(bl, P, 1.6)}/>` +
    `<path d="M48 72C48 82 46 90 46 97L54 94C54 86 54 80 56 72Z" fill="url(#${p}y)" ${ol(y, P, 1.6)}/>` +
    `<path d="M20 78H82" stroke="${edge(wood(P), P)}" stroke-width="8" stroke-linecap="round"/><path d="M20 78H82" stroke="url(#${p}w)" stroke-width="5.4" stroke-linecap="round"/>` +
    `<path d="M37 26C40 18 46 14 50 16C48 12 52 8 56 10C58 7 62 8 62 12C70 14 74 22 72 32C72 44 66 56 60 66C56 74 46 78 40 74C30 66 30 44 37 26Z" fill="url(#${p}b)" ${ol(r, P, 2)}/>` +
    `<path d="M38 42C48 38 56 46 54 58C52 68 46 74 40 74C34 70 32 54 38 42Z" fill="url(#${p}y)" ${ol(y, P, 1.6)}/>` +
    `<path d="M38 56C44 56 50 62 48 72C44 76 40 76 38 72C35 66 35 60 38 56Z" fill="url(#${p}u)" ${ol(bl, P, 1.6)}/>` +
    `<path d="M41 62Q44 66 44 72M38 52Q46 50 50 56" fill="none" stroke="${edge(bl, P)}" stroke-width="1.2" stroke-opacity=".6" stroke-linecap="round"/>` +
    `<ellipse cx="58" cy="27" rx="9" ry="8" fill="#FFFFFF" fill-opacity="${P.dark ? 0.9 : 1}"/>` +
    eye(59, 26, 4.2) +
    `<path d="M67 22C78 20 86 28 82 40C80 36 76 34 70 35C68 31 67 27 67 22Z" fill="url(#${p}k)" ${ol(beak, P, 1.8)}/>` +
    `<path d="M69 35C74 34 77 36 76 40C73 42 70 40 69 35Z" fill="${INK}" fill-opacity=".8"/>` +
    `<path d="M44 74Q42 80 46 82M54 72Q54 80 58 82" fill="none" stroke="${P.dark ? '#9AA6B8' : '#6B7686'}" stroke-width="3" stroke-linecap="round"/>` +
    gloss(46, 24, 7, 3.5, -40, 0.55)
  );
};

/** brass compass: a ring with a hanging loop, a cream face with a four-point rose and a red / steel needle */
const pcCompass: Draw = (P, p) => {
  const b = warm(P);
  const face = P.dark ? '#F3E9D2' : '#FFF8E8';
  const ticks = Array.from({ length: 8 }, (_, i) => {
    const a = (i * Math.PI) / 4;
    const r0 = i % 2 ? 27 : 24;
    return `M${n(50 + r0 * Math.cos(a))} ${n(54 + r0 * Math.sin(a))}L${n(50 + 30 * Math.cos(a))} ${n(54 + 30 * Math.sin(a))}`;
  }).join('');
  return (
    D(drop(P, p), body(`${p}b`, b, 0.55, 0.42, 0.6, 1), ball(`${p}f`, face, 0.5, 0.18)) +
    ground(p, 50, 93, 30, 4.5) +
    `<circle cx="50" cy="12" r="6" fill="none" stroke="${edge(b, P)}" stroke-width="5"/><circle cx="50" cy="12" r="6" fill="none" stroke="url(#${p}b)" stroke-width="3"/>` +
    `<rect x="44" y="16" width="12" height="8" rx="3" fill="url(#${p}b)" ${ol(b, P, 1.6)}/>` +
    `<circle cx="50" cy="54" r="38" fill="url(#${p}b)" ${ol(b, P, 2.2)}/>` +
    `<circle cx="50" cy="54" r="31" fill="url(#${p}f)" ${ol(b, P, 1.6)}/>` +
    `<path d="${ticks}" stroke="${shade(b, 0.4)}" stroke-width="1.8" stroke-linecap="round"/>` +
    `<polygon points="${star(50, 54, 20, 4, 4)}" fill="${P.teal}" fill-opacity=".35"/>` +
    `<polygon points="${star(50, 54, 12, 3, 4, -Math.PI / 4)}" fill="${P.teal}" fill-opacity=".25"/>` +
    `<g transform="rotate(28 50 54)"><path d="M50 30L56 54H44Z" fill="${P.red}" ${ol(P.red, P, 1.4)}/>` +
    `<path d="M44 54H56L50 78Z" fill="${steel(P)}" ${ol(steel(P), P, 1.4)}/></g>` +
    `<circle cx="50" cy="54" r="4" fill="url(#${p}b)" ${ol(b, P, 1.2)}/>` +
    gloss(36, 38, 12, 5, -40, 0.5) +
    gloss(30, 30, 6, 2.5, -45, 0.6)
  );
};

/** spyglass: leather-wrapped barrel with brass rims, two brass draw tubes and a glinting lens */
const pcSpyglass: Draw = (P, p) => {
  const b = warm(P);
  const lea = shade(mix(P.orange, P.red, 0.35), P.dark ? 0.3 : 0.2);
  const rim = (x: number, h: number) =>
    `<rect x="${x}" y="${-h / 2}" width="6" height="${h}" rx="2" fill="url(#${p}b)" ${ol(b, P, 1.4)}/>`;
  return (
    D(
      drop(P, p),
      `<linearGradient id="${p}b" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${tint(b, 0.55)}"/><stop offset=".4" stop-color="${b}"/><stop offset="1" stop-color="${shade(b, 0.4)}"/></linearGradient>`,
      `<linearGradient id="${p}l" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="${tint(lea, 0.35)}"/><stop offset=".45" stop-color="${lea}"/><stop offset="1" stop-color="${shade(lea, 0.4)}"/></linearGradient>`,
      ball(`${p}g`, P.sky, 0.6, 0.3),
    ) +
    ground(p, 50, 88, 36, 5) +
    `<g transform="translate(50 52) rotate(-32)">` +
    `<rect x="-46" y="-5" width="10" height="10" rx="2" fill="${shade(b, 0.45)}" ${ol(b, P, 1.2)}/>` +
    `<rect x="-38" y="-7" width="22" height="14" rx="1" fill="url(#${p}b)" ${ol(b, P, 1.4)}/>` +
    `<rect x="-18" y="-9.5" width="20" height="19" rx="1" fill="url(#${p}b)" ${ol(b, P, 1.4)}/>` +
    `<path d="M2 -12L36 -14V14L2 12Z" fill="url(#${p}l)" ${ol(lea, P, 1.6)}/>` +
    `<path d="M10 -12L14 12M18 -13L22 13M26 -13L30 13" stroke="${shade(lea, 0.35)}" stroke-width="1.2" stroke-opacity=".6"/>` +
    rim(-2, 26) +
    rim(34, 32) +
    rim(-22, 21) +
    `<ellipse cx="41" cy="0" rx="4" ry="14" fill="url(#${p}g)" ${ol(b, P, 1.8)}/>` +
    `<path d="M-36 -4H-18M-16 -6H0M4 -9H32" stroke="#FFFFFF" stroke-opacity=".55" stroke-width="1.8" stroke-linecap="round"/>` +
    `</g>` +
    `<polygon points="${star(80, 26, 7, 2, 4)}" fill="#FFFFFF" fill-opacity=".9"/>`
  );
};

/** anchor: a friendly navy anchor with a ring, a stock and two flukes, a sandy rope looped through the ring */
const pcAnchor: Draw = (P, p) => {
  const c = P.dark ? mix(P.blue, P.sky, 0.3) : P.blue;
  const rope = P.dark ? '#E6CB94' : '#D9B26A';
  const arm = 'M20 58Q22 82 50 84Q78 82 80 58';
  const twist = 'M56 14C70 14 74 28 62 38C52 46 44 44 42 52C40 60 54 62 60 68';
  const stroke2 = (d: string, w: number) =>
    `<path d="${d}" fill="none" stroke="${edge(c, P)}" stroke-width="${w + 3}" stroke-linecap="round"/>` +
    `<path d="${d}" fill="none" stroke="url(#${p}b)" stroke-width="${w}" stroke-linecap="round"/>`;
  return (
    D(drop(P, p), body(`${p}b`, c, 0.45, 0.4, 0.8, 1)) +
    ground(p, 50, 93, 30, 4.5) +
    `<circle cx="50" cy="16" r="8" fill="none" stroke="${edge(c, P)}" stroke-width="8"/><circle cx="50" cy="16" r="8" fill="none" stroke="url(#${p}b)" stroke-width="5"/>` +
    stroke2('M50 24V82', 8) +
    stroke2(arm, 7) +
    stroke2('M32 32H68', 7) +
    `<path d="M10 62L20 50L28 62Z" fill="url(#${p}b)" ${ol(c, P, 2)}/>` +
    `<path d="M90 62L80 50L72 62Z" fill="url(#${p}b)" ${ol(c, P, 2)}/>` +
    `<circle cx="50" cy="84" r="5" fill="url(#${p}b)" ${ol(c, P, 1.6)}/>` +
    `<path d="${twist}" fill="none" stroke="${shade(rope, 0.4)}" stroke-width="6" stroke-linecap="round"/>` +
    `<path d="${twist}" fill="none" stroke="${rope}" stroke-width="4" stroke-linecap="round"/>` +
    `<path d="${twist}" fill="none" stroke="${shade(rope, 0.35)}" stroke-width="4" stroke-dasharray="1.4 3"/>` +
    `<path d="M60 68L66 74M60 68L64 76" stroke="${rope}" stroke-width="2" stroke-linecap="round"/>` +
    gloss(46, 46, 1.6, 14, 0, 0.5) +
    gloss(44, 12, 3, 2, -30, 0.6)
  );
};

/** open scallop with a pearl (not the closed Deep shell): a nacre-lined upper valve, a coral lower valve, ribs */
const pcShell: Draw = (P, p) => {
  const c = P.dark ? mix(P.orange, P.pink, 0.45) : mix(P.orange, P.pink, 0.4);
  const up = 'M50 62L14 38Q12 28 21 25Q23 14 35 14Q41 7 50 8Q59 7 65 14Q77 14 79 25Q88 28 86 38Z';
  const ribs = 'M50 60L20 28M50 60L32 17M50 60L50 12M50 60L68 17M50 60L80 28';
  return (
    D(
      drop(P, p),
      `<linearGradient id="${p}n" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="${tint(P.pink, 0.7)}"/><stop offset=".35" stop-color="${tint(P.sky, 0.65)}"/><stop offset=".65" stop-color="${tint(P.purple, 0.6)}"/><stop offset="1" stop-color="${tint(P.teal, 0.6)}"/></linearGradient>`,
      body(`${p}b`, c, 0.45, 0.35, 0.4, 1),
      ball(`${p}p`, '#FFFFFF', 0, 0.22),
    ) +
    ground(p, 50, 92, 32, 4.5) +
    `<path d="${up}" fill="url(#${p}n)" ${ol(c, P, 2.2)}/>` +
    `<path d="${ribs}" fill="none" stroke="#FFFFFF" stroke-opacity=".6" stroke-width="2" stroke-linecap="round"/>` +
    `<circle cx="50" cy="53" r="12" fill="url(#${p}p)" stroke="${shade(P.purple, 0.2)}" stroke-opacity=".45" stroke-width="1.4"/>` +
    gloss(46, 50, 4, 2.6, -35, 0.95) +
    `<path d="M12 58Q50 72 88 58Q82 80 58 90Q50 93 42 90Q18 80 12 58Z" ${fl(p, 'b')} ${ol(c, P, 2.2)}/>` +
    `<path d="M24 64Q32 80 46 89M37 68Q41 80 48 90M50 70V91M63 68Q59 80 52 90M76 64Q68 80 54 89" fill="none" stroke="${edge(c, P)}" stroke-width="1.6" stroke-opacity=".5" stroke-linecap="round"/>` +
    gloss(28, 68, 7, 2.5, 20, 0.5) +
    gloss(34, 24, 8, 3, -30, 0.55)
  );
};

const DRAW = {
  carrot,
  tomato,
  pumpkin,
  radish,
  corn,
  can,
  scarecrow,
  sun,
  shell,
  starfish,
  jelly,
  fishA,
  fishB,
  fishC,
  pearl,
  bubble,
  planetR,
  planetB,
  planetG,
  planetO,
  satellite,
  astronaut,
  ufo,
  blackhole,
  comet,
  donut,
  cupcake,
  lollipop,
  gummy,
  macaron,
  jar,
  jar_mini,
  jar_major,
  jar_grand,
  gear,
  wrench,
  bulb,
  blueprint,
  robot,
  part,
  battery,
  hammer,
  koi,
  moonBridge,
  lotus,
  lantern,
  mandarin,
  fan,
  bamboo,
  jadeCoin,
  rank10,
  rank9,
  rank8,
  llPeach,
  llGourd,
  llLantern,
  llKoi,
  llJade,
  llIngot,
  llDragon,
  pcChest,
  pcMap,
  pcParrot,
  pcCompass,
  pcSpyglass,
  pcAnchor,
  pcShell,
  ...NEON_DRAW,
} satisfies Record<string, Draw>;

export type SymbolName = keyof typeof DRAW;

export const SYMBOL_NAMES = Object.keys(DRAW) as SymbolName[];

/** Prefix of every <defs> id of a symbol: unique per symbol + theme. */
const idPrefix = (name: SymbolName, theme: ArtTheme) => `s-${name}-${theme[0]}-`;

const LIB: Record<ArtTheme, Partial<Record<SymbolName, string>>> = { dark: {}, light: {} };

const uri = (svg: string) => 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);

/** Inner SVG markup (viewBox 0 0 100 100) — for canvas/SVG reels, inlinable next to other art. */
export function symbolMarkup(name: SymbolName, theme: ArtTheme = 'dark'): string {
  const draw = DRAW[name];
  if (!draw) return '';
  return (LIB[theme][name] ??= draw(ART_PAL[theme], idPrefix(name, theme)));
}

/** Standalone SVG document of a symbol (100×100). */
export function symbolSvg(name: SymbolName, theme: ArtTheme = 'dark'): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">${symbolMarkup(name, theme)}</svg>`;
}

/**
 * URL of a symbol: the raster art (one for both themes, @2x when `scale` is 2 — by default on
 * devicePixelRatio > 1) once `pnpm art:process` has produced it, else the data-URI of the 100×100 vector art.
 */
export function symbolUri(
  name: SymbolName,
  theme: ArtTheme = 'dark',
  scale: RasterScale = currentScale(),
): string {
  const raster = rasterSymbolByName(name, scale);
  if (raster) return raster;
  return uri(symbolSvg(name, theme));
}

/** Koi Pond ranks over the raster jade medallions (`koi-ranks/disc-*`, Codex task 4: the recess is empty). */
const RANK_DISC = { rank10: 'disc-teal', rank9: 'disc-sky', rank8: 'disc-purple' } as const;
type RankName = keyof typeof RANK_DISC;
const RANKS = Object.keys(RANK_DISC) as RankName[];
/** median colour of each raster jade face — the carving is cut into it (same picture in both themes) */
const RANK_JADE: Record<RankName, string> = { rank10: '#2FCAB8', rank9: '#5CCAFA', rank8: '#AD8DF8' };
/** the jade face of the raster discs sits a little high: centre ≈ (50, 45.7), radius ≈ 39 of the 100 box */
const RANK_FACE_DY = -4.5;

const isRank = (name: string): name is RankName => name in RANK_DISC;

/** The carved sign alone (viewBox 0 0 100 100) to lay over a raster disc: dark groove + light lower edge. */
export function rankOverlaySvg(name: RankName): string {
  const c = RANK_JADE[name];
  const d = RANK_GLYPH[name];
  const line = 'fill="none" stroke-linecap="round" stroke-linejoin="round"';
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><g transform="translate(0 ${RANK_FACE_DY})">` +
    `<path d="${d}" transform="translate(-.6 -.9)" ${line} stroke="${shade(c, 0.75)}" stroke-opacity=".35" stroke-width="9"/>` +
    `<path d="${d}" ${line} stroke="${shade(c, 0.55)}" stroke-width="8"/>` +
    `<path d="${d}" transform="translate(.8 1.2)" ${line} stroke="${tint(c, 0.7)}" stroke-opacity=".45" stroke-width="3"/>` +
    `</g></svg>`
  );
}

/**
 * What to draw for a symbol: `uri` (as symbolUri) over an optional raster `under` it. Koi Pond ranks without a
 * raster of their own, when all three jade discs exist, are the disc under a carved SVG sign; otherwise one picture.
 */
export function symbolLayers(
  name: SymbolName,
  theme: ArtTheme = 'dark',
  scale: RasterScale = currentScale(),
): { under: string | null; uri: string } {
  if (
    isRank(name) &&
    !rasterSymbolByName(name, scale) &&
    RANKS.every((r) => rasterArt('koi-ranks', RANK_DISC[r], scale))
  )
    return { under: rasterArt('koi-ranks', RANK_DISC[name], scale), uri: uri(rankOverlaySvg(name)) };
  return { under: null, uri: symbolUri(name, theme, scale) };
}

/**
 * Data-URI of a production cover (see ./covers), cropped to `w`×`h`: the 16:10 art is anchored right so the
 * focus object stays visible in square / narrow tiles. Accepts every game (and 'crash-hero').
 */
export function coverUri(game: CoverId, w = 200, h = 240, theme: ArtTheme = 'dark'): string {
  return uri(coverSvgSized(game, w, h, theme));
}
